<template>
  <div class="modal-overlay" v-if="showModal" @click.self="closeModal">
    <div class="modal-content">
      <div class="modal-header">
        <div class="header-row">
          <div class="header-left"></div>
          <div class="tabs">
            <button
              v-for="tab in availableTabs"
              :key="tab.id"
              :class="['tab-button', { active: activeTab === tab.id }]"
              @click="changeTab(tab.id)"
            >
              <span>{{ tab.label }}</span>
            </button>
          </div>
          <div class="header-right">
            <button class="close-button" @click="closeModal">
              <span>✕</span>
            </button>
          </div>
        </div>
      </div>

      <div class="tab-content">
        <h2 v-if="!isFlashMode" class="content-title"><strong>{{ pointData.point }}</strong></h2>
        <div class="main-content-container">
          <div class="main-content">

            <div v-if="activeTab === 'response'" class="article-content">
              <div ref="responseContent" class="article-html" v-html="formatText(pointData.final_answer, false)" @click.prevent="handleInlineReferenceClick"></div>
            </div>

            <div v-if="activeTab === 'energy'" class="article-content">
              <div class="source-block" v-if="getEnergySources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getEnergySources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.energyDecision', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'energy',
                        doc_id: doc.doc_id,
                        url: doc.url,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.energyDecision', { n: docIndex + 1 }) }}
                    </span>

                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.energy') }}
              </div>
            </div>

            <div v-if="activeTab === 'concurrence'" class="article-content">
              <div class="source-block" v-if="getConcurrenceSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getConcurrenceSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.titre || doc.numero_affaire || $t('modals.legalAnalysis.fallbacks.decision', { n: docIndex + 1 }),
                        content: doc.texte_integral || doc.content,
                        source: 'concurrence',
                        id_document: doc.id_document,
                        type_document: doc.type_document,
                        numero_affaire: doc.numero_affaire,
                        date_decision: doc.date_decision,
                        secteur: doc.secteur,
                        parties: doc.parties,
                        sens_decision: doc.sens_decision,
                        url_page: doc.url_page,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.titre || doc.numero_affaire || $t('modals.legalAnalysis.fallbacks.decision', { n: docIndex + 1 }) }}
                    </span>
                    <div class="concurrence-preview" v-if="doc.type_document || doc.secteur">
                      <span v-if="doc.type_document" class="concurrence-type">{{ doc.type_document }}</span>
                      <span v-if="doc.secteur" class="concurrence-sector">{{ doc.secteur }}</span>
                      <span v-if="doc.date_decision" class="concurrence-date">{{ formatConcurrenceDate(doc.date_decision) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.concurrence') }}
              </div>
            </div>

            <div v-if="activeTab === 'inpi'" class="article-content">
              <div class="source-block" v-if="getInpiSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getInpiSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.reference || doc.parties || $t('modals.legalAnalysis.fallbacks.inpiDecision', { n: docIndex + 1 }),
                        content: doc.texte || doc.text_complet || doc.content || '',
                        source: 'inpi',
                        id_doc: doc.id_doc,
                        domaine: doc.domaine,
                        reference: doc.reference,
                        juridiction: doc.juridiction,
                        parties: doc.parties,
                        date: doc.date,
                        url_document_final: doc.url_document_final,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.reference || doc.parties || $t('modals.legalAnalysis.fallbacks.inpiDecision', { n: docIndex + 1 }) }}
                    </span>
                    <div class="inpi-preview" v-if="doc.domaine || doc.juridiction">
                      <span v-if="doc.domaine" :class="['inpi-domain', getInpiDomainClass(doc.domaine)]">{{ doc.domaine }}</span>
                      <span v-if="doc.juridiction" class="inpi-jurisdiction">{{ doc.juridiction }}</span>
                      <span v-if="doc.date" class="inpi-date">{{ formatInpiDate(doc.date) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.inpi') }}
              </div>
            </div>

            <div v-if="activeTab === 'cnil'" class="article-content">
              <div class="source-block" v-if="getCnilSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getCnilSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.cnilDecision', { n: docIndex + 1 }),
                        content: doc.texte || doc.full_text || doc.content || '',
                        source: 'cnil',
                        nature: doc.nature,
                        numero: doc.numero,
                        date: doc.date,
                        titre_complet: doc.titre_complet,
                        url: doc.link,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.cnilDecision', { n: docIndex + 1 }) }}
                    </span>
                    <div class="cnil-preview" v-if="doc.nature || doc.date">
                      <span v-if="doc.nature" class="cnil-nature-badge">{{ doc.nature }}</span>
                      <span v-if="doc.date" class="cnil-date">{{ formatCnilDate(doc.date) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.cnil') }}
              </div>
            </div>

            <div v-if="activeTab === 'boss'" class="article-content">
              <div class="source-block" v-if="getBossSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getBossSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.bossSection', { n: docIndex + 1 }),
                        content: doc.texte || doc.full_text || doc.content || '',
                        source: 'boss',
                        date: doc.date,
                        url: doc.link || doc.url,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.bossSection', { n: docIndex + 1 }) }}
                    </span>
                    <div class="boss-preview" v-if="doc.date">
                      <span v-if="doc.date" class="boss-date">{{ doc.date ? new Date(doc.date).getFullYear() : '' }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.boss') }}
              </div>
            </div>

                  <div v-if="activeTab === 'acpr'" class="article-content">
                    <div class="source-block" v-if="getAcprSources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getAcprSources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.title || $t('modals.legalAnalysis.fallbacks.acprDocument', { n: docIndex + 1 }),
                              content: doc.summary || '',
                              source: 'acpr',
                              acpr_source: doc.acpr_source,
                              doc_subtype: doc.doc_subtype,
                              categorie: doc.categorie,
                              themes: doc.themes,
                              numero: doc.numero,
                              date: doc.date,
                              url: doc.link,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.acprDocument', { n: docIndex + 1 }) }}
                          </span>
                          <div class="acpr-preview" v-if="doc.acpr_source || doc.date">
                            <span v-if="doc.acpr_source" class="acpr-source-badge">{{ doc.acpr_source }}</span>
                            <span v-if="doc.date" class="acpr-date">{{ formatAcprDate(doc.date) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.acpr') }}
                    </div>
                  </div>

                  <div v-if="activeTab === 'amf'" class="article-content">
                    <div class="source-block" v-if="getAmfSources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getAmfSources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.title || $t('modals.legalAnalysis.fallbacks.amfDocument', { n: docIndex + 1 }),
                              content: doc.summary || '',
                              source: 'amf',
                              amf_source: doc.amf_source,
                              doc_type: doc.doc_type,
                              reference: doc.reference,
                              themes: doc.themes,
                              montant: doc.montant,
                              sanctioned_entity: doc.sanctioned_entity,
                              date: doc.date,
                              url: doc.link,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.amfDocument', { n: docIndex + 1 }) }}
                          </span>
                          <div class="amf-preview" v-if="doc.amf_source || doc.date">
                            <span v-if="doc.amf_source" class="amf-source-badge">{{ doc.amf_source }}</span>
                            <span v-if="doc.date" class="amf-date">{{ doc.date }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.amf') }}
                    </div>
                  </div>

                  <div v-if="activeTab === 'eccomp'" class="article-content">
                    <div class="source-block" v-if="getEccompSources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getEccompSources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.title || doc.case_title || doc.case_number || $t('modals.legalAnalysis.fallbacks.ccDecision', { n: docIndex + 1 }),
                              content: doc.summary || '',
                              source: 'eccomp',
                              case_number: doc.case_number,
                              case_title: doc.case_title,
                              instrument: doc.instrument,
                              decision_type: doc.decision_type,
                              decision_reference: doc.decision_reference,
                              decision_language: doc.decision_language,
                              member_state: doc.member_state,
                              companies: doc.companies,
                              legal_basis: doc.legal_basis,
                              sectors: doc.sectors,
                              date: doc.date,
                              year: doc.year,
                              url: doc.link,
                              texte: doc.full_text || doc.texte,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.title || doc.case_title || doc.case_number || $t('modals.legalAnalysis.fallbacks.ccDecision', { n: docIndex + 1 }) }}
                          </span>
                          <div class="eccomp-preview" v-if="doc.instrument || doc.date">
                            <span v-if="doc.instrument" class="eccomp-instrument-badge">{{ doc.instrument }}</span>
                            <span v-if="doc.case_number" class="eccomp-case-number">{{ doc.case_number }}</span>
                            <span v-if="doc.date" class="eccomp-date">{{ doc.date }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.eccomp') }}
                    </div>
                  </div>

                  <div v-if="activeTab === 'upc'" class="article-content">
                    <div class="source-block" v-if="getUpcSources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getUpcSources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.parties || (doc.court && (doc.date_raw || doc.date) ? (doc.court + ' - ' + (doc.date_raw || doc.date)) : null) || $t('modals.legalAnalysis.fallbacks.upcDecision', { n: docIndex + 1 }),
                              content: doc.summary || doc.headnotes || '',
                              source: 'upc',
                              case_numbers: doc.case_numbers,
                              registry_number: doc.registry_number,
                              court: doc.court,
                              action_type: doc.action_type,
                              parties: doc.parties,
                              date: doc.date_raw || doc.date,
                              language_of_proceedings: doc.language_of_proceedings,
                              document_language: doc.document_language,
                              summary: doc.summary,
                              headnotes: doc.headnotes,
                              keywords: doc.keywords,
                              detail_url: doc.detail_url,
                              pdf_url: doc.pdf_url,
                              url: doc.detail_url || doc.pdf_url,
                              texte: doc.texte,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.parties || (doc.court && (doc.date_raw || doc.date) ? (doc.court + ' - ' + (doc.date_raw || doc.date)) : null) || $t('modals.legalAnalysis.fallbacks.upcDecision', { n: docIndex + 1 }) }}
                          </span>
                          <div class="upc-preview" v-if="doc.court || doc.action_type || doc.date_raw">
                            <span v-if="doc.court" class="upc-court-badge">{{ doc.court }}</span>
                            <span v-if="doc.action_type" class="upc-action-type">{{ doc.action_type }}</span>
                            <span v-if="doc.date_raw" class="upc-date">{{ doc.date_raw }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.upc') }}
                    </div>
                  </div>

                  <div v-if="activeTab === 'edpb'" class="article-content">
                    <div class="source-block" v-if="getEdpbSources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getEdpbSources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.title || $t('modals.legalAnalysis.fallbacks.edpbDecision', { n: docIndex + 1 }),
                              content: doc.summary || doc.key_points || '',
                              source: 'edpb',
                              id_document: doc.id || doc.azure_id,
                              slug: doc.slug,
                              publication_types: doc.publication_types,
                              topics: doc.topics,
                              members: doc.members,
                              status: doc.status,
                              date: doc.date_raw || doc.date,
                              summary: doc.summary,
                              key_points: doc.key_points,
                              keywords: doc.keywords,
                              url: doc.url,
                              pdf_url_used: doc.pdf_url_used,
                              word_count: doc.word_count,
                              texte: doc.texte,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.edpbDecision', { n: docIndex + 1 }) }}
                          </span>
                          <div class="edpb-preview" v-if="(doc.publication_types && doc.publication_types.length) || doc.date_raw">
                            <span v-for="(pt, ptIdx) in (doc.publication_types || []).slice(0, 2)" :key="ptIdx" class="edpb-type-badge">{{ pt }}</span>
                            <span v-if="doc.date_raw" class="edpb-date">{{ doc.date_raw }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.edpb') }}
                    </div>
                  </div>

                  <div v-if="activeTab === 'agcm'" class="article-content">
                    <div class="source-block" v-if="getAgcmSources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getAgcmSources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.title || $t('modals.legalAnalysis.fallbacks.agcmDecision', { n: docIndex + 1 }),
                              content: doc.summary || doc.takeaway || '',
                              source: 'agcm',
                              date: doc.date,
                              year: doc.year,
                              rid: doc.rid,
                              url: doc.link,
                              texte: doc.texte || doc.full_text,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.agcmDecision', { n: docIndex + 1 }) }}
                          </span>
                          <div class="agcm-preview" v-if="doc.date || doc.rid">
                            <span v-if="doc.rid" class="agcm-rid">{{ doc.rid }}</span>
                            <span v-if="doc.date" class="agcm-date">{{ doc.date }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.agcm') }}
                    </div>
                  </div>

                  <div v-if="activeTab === 'giustizia_amministrativa'" class="article-content">
                    <div class="source-block" v-if="getGiustiziaAmministrativaSources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getGiustiziaAmministrativaSources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.title || $t('modals.legalAnalysis.fallbacks.giustiziaAmministrativaDecision', { n: docIndex + 1 }),
                              content: doc.summary || doc.takeaway || '',
                              source: 'giustizia_amministrativa',
                              court: doc.court,
                              section: doc.section,
                              tipo: doc.tipo,
                              date: doc.date,
                              year: doc.year,
                              rid: doc.rid,
                              url: doc.link,
                              texte: doc.texte || doc.full_text,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.giustiziaAmministrativaDecision', { n: docIndex + 1 }) }}
                          </span>
                          <div class="ga-preview" v-if="doc.court || doc.tipo || doc.date">
                            <span v-if="doc.court" class="ga-court">{{ doc.court }}</span>
                            <span v-if="doc.tipo" class="ga-tipo">{{ doc.tipo }}</span>
                            <span v-if="doc.date" class="ga-date">{{ doc.date }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.giustiziaAmministrativa') }}
                    </div>
                  </div>

                  <div v-if="activeTab === 'jurisprudence_europe_italy'" class="article-content">
                    <div class="source-block" v-if="getJurisprudenceEuropeItalySources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getJurisprudenceEuropeItalySources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.title || $t('modals.legalAnalysis.fallbacks.jurisprudenceEuropeItalyDecision', { n: docIndex + 1 }),
                              content: doc.summary || doc.takeaway || '',
                              source: 'jurisprudence_europe_italy',
                              court: doc.court,
                              celex: doc.celex,
                              ecli: doc.ecli,
                              date: doc.date,
                              year: doc.year,
                              rid: doc.rid,
                              url: doc.link,
                              texte: doc.texte || doc.full_text,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.jurisprudenceEuropeItalyDecision', { n: docIndex + 1 }) }}
                          </span>
                          <div class="euit-preview" v-if="doc.celex || doc.ecli || doc.date">
                            <span v-if="doc.celex" class="euit-celex">{{ doc.celex }}</span>
                            <span v-if="doc.ecli" class="euit-ecli">{{ doc.ecli }}</span>
                            <span v-if="doc.date" class="euit-date">{{ doc.date }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.jurisprudenceEuropeItaly') }}
                    </div>
                  </div>

                  <div v-if="activeTab === 'eurlex_italy'" class="article-content">
                    <div class="source-block" v-if="getEurlexItalySources().length">
                      <div class="source-items">
                        <div v-for="(doc, docIndex) in getEurlexItalySources()" :key="docIndex" class="source-item">
                          <span
                            class="article-link"
                            @click="showArticleDetails({
                              title: doc.title || $t('modals.legalAnalysis.fallbacks.eurlexItalyArticle', { n: docIndex + 1 }),
                              content: doc.summary || doc.takeaway || '',
                              source: 'eurlex_italy',
                              base_celex: doc.base_celex,
                              cons_celex: doc.cons_celex,
                              consolidation_date: doc.consolidation_date,
                              act_type: doc.act_type,
                              act_number: doc.act_number,
                              act_year: doc.act_year,
                              act_title: doc.act_title,
                              article_identifier: doc.article_identifier,
                              article_heading: doc.article_heading,
                              article_title: doc.article_title,
                              breadcrumb: doc.breadcrumb,
                              paragraphs_count: doc.paragraphs_count,
                              jurisdiction: doc.jurisdiction,
                              eli: doc.eli,
                              formex_schema: doc.formex_schema,
                              date: doc.date,
                              year: doc.year,
                              url: doc.link,
                              texte: doc.texte || doc.full_text,
                              sourceData: doc
                            })"
                          >
                            {{ docIndex + 1 }}. {{ doc.article_heading ? (doc.article_heading + (doc.article_title ? ' — ' + doc.article_title : '')) : (doc.title || $t('modals.legalAnalysis.fallbacks.eurlexItalyArticle', { n: docIndex + 1 })) }}
                          </span>
                          <div class="eurlex-preview" v-if="doc.cons_celex || doc.consolidation_date || doc.breadcrumb">
                            <span v-if="doc.cons_celex" class="eurlex-celex">{{ doc.cons_celex }}</span>
                            <span v-if="doc.consolidation_date" class="eurlex-date">{{ doc.consolidation_date }}</span>
                            <span v-if="doc.breadcrumb" class="eurlex-breadcrumb-preview">{{ doc.breadcrumb }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-message">
                      {{ $t('modals.legalAnalysis.emptyState.eurlexItaly') }}
                    </div>
                  </div>

            <div v-if="activeTab === 'mention'" class="article-content">
              <div class="source-block" v-if="getMentionSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getMentionSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: getMentionTitle(doc) || $t('modals.legalAnalysis.fallbacks.citedDocument', { n: docIndex + 1 }),
                        content: doc.content || doc.takeaway || doc.full_text || '',
                        url: doc.link || doc.official_link,
                        source: 'mention',
                        type: doc.type,
                        date: doc.date,
                        jurisdiction: doc.jurisdiction,
                        original_reference: doc.original_reference,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ getMentionTitle(doc) || $t('modals.legalAnalysis.fallbacks.citedDocument', { n: docIndex + 1 }) }}
                    </span>
                    <div class="mention-preview" v-if="doc.jurisdiction || doc.date">
                      <span v-if="doc.type" class="mention-type">{{ doc.type }}</span>
                      <span v-if="doc.jurisdiction" class="mention-jurisdiction">{{ doc.jurisdiction }}</span>
                      <span v-if="doc.date" class="mention-date">{{ formatMentionDate(doc.date) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.mention') }}
              </div>
            </div>

            <div v-if="activeTab === 'reglements'" class="article-content">
              <div class="source-block" v-if="getReglementsSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getReglementsSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || formatReglementsTitle(doc),
                        content: doc.texte || doc.content || '',
                        source: 'reglements',
                        celex: doc.celex,
                        article_number: doc.article_number,
                        chapter_title: doc.chapter_title,
                        date: doc.date,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title }}
                    </span>
                    <div class="reglements-preview" v-if="doc.chapter_title">
                      <span class="reglements-chapter">{{ doc.chapter_title }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.reglements') }}
              </div>
            </div>

            <div v-if="activeTab === 'bodacc'" class="article-content">
              <div class="source-block" v-if="getBodaccSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getBodaccSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.bodaccAnnouncement', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'bodacc',
                        doc_id: doc.doc_id,
                        dateparution: doc.dateparution,
                        familleavis: doc.familleavis,
                        typeavis: doc.typeavis,
                        url_complete: doc.url_complete,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.bodaccAnnouncement', { n: docIndex + 1 }) }}
                    </span>
                    <div class="bodacc-preview" v-if="doc.familleavis || doc.typeavis">
                      <span v-if="doc.familleavis" class="bodacc-family">{{ formatBodaccFamily(doc.familleavis) }}</span>
                      <span v-if="doc.typeavis" class="bodacc-type">{{ formatBodaccType(doc.typeavis) }}</span>
                      <span v-if="doc.dateparution" class="bodacc-date">{{ formatBodaccDate(doc.dateparution) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.bodacc') }}
              </div>
            </div>

            <div v-if="activeTab === 'actes_societes'" class="article-content">
              <div class="source-block" v-if="getActesSocietesSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getActesSocietesSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.companyAct', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'actes_societes',
                        siren: doc.siren,
                        acte_id: doc.acte_id,
                        date_acte: doc.date_acte,
                        summary: doc.summary,
                        keywords: doc.keywords,
                        chunk_number: doc.chunk_number,
                        total_pages: doc.total_pages,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.companyAct', { n: docIndex + 1 }) }}
                    </span>
                    <div class="actes-societes-preview" v-if="doc.siren || doc.date_acte">
                      <span v-if="doc.siren" class="actes-societes-siren">{{ $t('modals.legalAnalysis.labels.sirenInline', { siren: doc.siren }) }}</span>
                      <span v-if="doc.date_acte" class="actes-societes-date">{{ formatActesSocietesDate(doc.date_acte) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.actesSocietes') }}
              </div>
            </div>

            <div v-if="activeTab === 'infofi'" class="article-content">
              <div class="source-block" v-if="getInfofiSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getInfofiSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.financialInfo', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'infofi',
                        nom_societe: doc.nom_societe,
                        'Titre opération': doc['Titre opération'], // <-- AJOUTER
                        Lien_Document: doc.Lien_Document, // <-- AJOUTER
                        date_document: doc.date_document,
                        description_information: doc.description_information,
                        id_information: doc.id_information,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.financialInfo', { n: docIndex + 1 }) }}
                    </span>
                    <div class="infofi-preview" v-if="doc.nom_societe || doc.date_document">
                      <span v-if="doc.nom_societe" class="infofi-company">{{ doc.nom_societe }}</span>
                      <span v-if="doc.date_document" class="infofi-date">{{ formatInfoFiDate(doc.date_document) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.infofi') }}
              </div>
            </div>

            <div v-if="activeTab === 'balo'" class="article-content">
              <div class="source-block" v-if="getBaloSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getBaloSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.baloAnnouncement', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'balo',
                        id_annonce: doc.id_annonce,
                        dateparution: doc.dateparution,
                        societes_noms: doc.societes_noms,
                        siren: doc.siren,
                        numero_affaire: doc.numero_affaire,
                        categorie_libelle: doc.categorie_libelle,
                        parution_numero: doc.parution_numero,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.baloAnnouncement', { n: docIndex + 1 }) }}
                    </span>
                    <div class="balo-preview" v-if="doc.societes_noms || doc.categorie_libelle">
                      <span v-if="doc.societes_noms" class="balo-company">{{ doc.societes_noms }}</span>
                      <span v-if="doc.categorie_libelle" class="balo-category">{{ formatBaloCategory(doc.categorie_libelle) }}</span>
                      <span v-if="doc.dateparution" class="balo-date">{{ formatBaloDate(doc.dateparution) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.balo') }}
              </div>
            </div>

            <div v-if="activeTab === 'convbi'" class="article-content">
              <div class="source-block" v-if="getConvbiSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getConvbiSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.convention', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'convbi',
                        pays: doc.pays,
                        titre_lien: doc.titre_lien,
                        pdf_url: doc.pdf_url,
                        article_num: doc.article_num,
                        texte: doc.texte,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.convention', { n: docIndex + 1 }) }}
                    </span>
                    <div class="convbi-preview" v-if="doc.pays">
                      <span class="convbi-country">{{ doc.pays }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.convbi') }}
              </div>
            </div>

            <div v-if="activeTab === 'cnn'" class="article-content">
              <div class="source-block" v-if="getCnnSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getCnnSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.convention', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'cnn',
                        etat: doc.etat,
                        date_debut: doc.date_debut,
                        date_fin: doc.date_fin,
                        cid_tex: doc.cid_tex,
                        article_id: doc.article_id,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.convention', { n: docIndex + 1 }) }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.cnn') }}
              </div>
            </div>

            <div v-if="activeTab === 'bofip'" class="article-content">
               <div class="source-block" v-if="getBofipSources().length">
                 <div class="source-items">
                   <div v-for="(doc, docIndex) in getBofipSources()" :key="docIndex" class="source-item">
                     <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.document', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'bofip',
                        url: doc.url,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.identifiant || $t('modals.legalAnalysis.fallbacks.document', { n: docIndex + 1 }) }}
                    </span>
                   </div>
                 </div>
               </div>
               <div v-else class="empty-message">
                 {{ $t('modals.legalAnalysis.emptyState.bofip') }}
               </div>
            </div>

            <div v-if="activeTab === 'euritext'" class="article-content">
               <div class="source-block" v-if="getEuritextSources().length">
                 <div class="source-items">
                   <div v-for="(doc, docIndex) in getEuritextSources()" :key="docIndex" class="source-item">
                     <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || $t('modals.legalAnalysis.fallbacks.document', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'euritext',
                        id: doc.id,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.document', { n: docIndex + 1 }) }}
                    </span>
                   </div>
                 </div>
               </div>
               <div v-else class="empty-message">
                 {{ $t('modals.legalAnalysis.emptyState.euritext') }}
               </div>
            </div>

            <div v-if="activeTab === 'juritext'" class="article-content">
               <div class="source-block" v-if="getJuritextSources().length">
                 <div class="source-items">
                   <div v-for="(doc, docIndex) in getJuritextSources()" :key="docIndex" class="source-item">
                    <span
                     class="article-link"
                     @click="showArticleDetails({
                       title: doc.title || $t('modals.legalAnalysis.fallbacks.decision', { n: docIndex + 1 }),
                       content: doc.summary,
                       source: 'juritext',
                       id: doc.id,
                       texte: doc.texte,
                       sourceData: doc
                     })"
                   >
                     {{ docIndex + 1 }}. {{ doc.title || $t('modals.legalAnalysis.fallbacks.decision', { n: docIndex + 1 }) }}
                   </span>
                   </div>
                 </div>
               </div>
               <div v-else class="empty-message">
                 {{ $t('modals.legalAnalysis.emptyState.juritext') }}
               </div>
            </div>

            <div v-if="activeTab === 'legiarti'" class="article-content">
               <div class="source-block" v-if="getLegiartiSources().length">
                 <div class="source-items">
                   <div v-for="(doc, docIndex) in getLegiartiSources()" :key="docIndex" class="source-item">
                     <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: doc.title || doc.textNumber || $t('modals.legalAnalysis.fallbacks.article', { n: docIndex + 1 }),
                        content: doc.content,
                        source: 'legiarti',
                        hierarchy: doc.hierarchy,
                        id: doc.id,
                        annotation: doc.annotation,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ doc.title || doc.textNumber || $t('modals.legalAnalysis.fallbacks.article', { n: docIndex + 1 }) }}
                    </span>
                   </div>
                 </div>
               </div>
               <div v-else class="empty-message">
                 {{ $t('modals.legalAnalysis.emptyState.legiarti') }}
               </div>
            </div>

            <div v-if="activeTab === 'statuts'" class="article-content">
              <div class="source-block" v-if="getStatutsSources().length">
                <div class="source-items">
                  <div v-for="(doc, docIndex) in getStatutsSources()" :key="docIndex" class="source-item">
                    <span
                      class="article-link"
                      @click="showArticleDetails({
                        title: getStatutsTitle(doc.pdf_name),
                        content: doc.content || '',
                        source: 'statuts',
                        pdf_name: doc.pdf_name,
                        slugged_title: doc.slugged_title,
                        sourceData: doc
                      })"
                    >
                      {{ docIndex + 1 }}. {{ getStatutsTitle(doc.pdf_name) }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="empty-message">
                {{ $t('modals.legalAnalysis.emptyState.statuts') }}
              </div>
            </div>
          </div>

          <div class="article-details bg-blue-color" v-if="showArticlePanel">
            <div class="details-header">
              <h3>{{ articleDetails.title }}</h3>
              <button class="close-details" @click="closeArticleDetails">✕</button>
            </div>

            <div class="scrollable-details-area">
              <div class="ref-section" v-if="hasReferences()">
                <h4 class="ref-title">{{ $t('modals.legalAnalysis.sections.references') }}</h4>

                <div v-if="articleDetails.source === 'infofi'" class="ref-content">
                  <div v-if="articleDetails.sourceData && articleDetails.sourceData.Lien_Document" class="source-url">
                    <a :href="articleDetails.sourceData.Lien_Document" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.sourcePdf') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'energy'" class="ref-content">
                  <div v-if="articleDetails.url" class="source-url">
                    <a :href="articleDetails.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewOnSite') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;">
                        <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'concurrence'" class="ref-content">
                  <div v-if="articleDetails.numero_affaire" class="concurrence-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.caseNumber') }}</strong>
                    <span class="concurrence-affaire">{{ articleDetails.numero_affaire }}</span>
                  </div>
                  <div v-if="articleDetails.url_page" class="source-url">
                    <a :href="articleDetails.url_page" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewConcurrenceSite') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;">
                        <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'cnil'" class="ref-content">
                  <div v-if="articleDetails.id" class="cnil-ref-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.identifier') }}</strong>
                    <span>{{ articleDetails.id }}</span>
                  </div>
                  <div v-if="articleDetails.url" class="source-url">
                    <a :href="articleDetails.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewCnilSite') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;">
                        <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'boss'" class="ref-content">
                  <div v-if="articleDetails.url" class="source-url">
                    <a :href="articleDetails.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewBossSite') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;">
                        <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/>
                      </svg>
                    </a>
                  </div>
                </div>

                    <div v-if="articleDetails.source === 'acpr'" class="ref-content">
                      <div v-if="articleDetails.id_document" class="acpr-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.identifier') }}</strong>
                        <span>{{ articleDetails.id_document }}</span>
                      </div>
                      <div v-if="articleDetails.numero" class="acpr-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.number') }}</strong>
                        <span>{{ articleDetails.numero }}</span>
                      </div>
                      <div v-if="articleDetails.url || (articleDetails.sourceData && articleDetails.sourceData.link)" class="source-url">
                        <a :href="articleDetails.url || articleDetails.sourceData.link" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                          <strong>{{ $t('modals.legalAnalysis.links.viewAcprSite') }}</strong>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                    </div>

                    <div v-if="articleDetails.source === 'amf'" class="ref-content">
                      <div v-if="articleDetails.reference" class="amf-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.reference') }}</strong>
                        <span>{{ articleDetails.reference }}</span>
                      </div>
                      <div v-if="articleDetails.url || (articleDetails.sourceData && articleDetails.sourceData.link)" class="source-url">
                        <a :href="articleDetails.url || articleDetails.sourceData.link" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                          <strong>{{ $t('modals.legalAnalysis.links.viewAmfSite') }}</strong>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                    </div>

                    <div v-if="articleDetails.source === 'eccomp'" class="ref-content">
                      <div v-if="articleDetails.case_number" class="eccomp-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.caseNumber') }}</strong>
                        <span>{{ articleDetails.case_number }}</span>
                      </div>
                      <div v-if="articleDetails.instrument" class="eccomp-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.instrument') }}</strong>
                        <span>{{ articleDetails.instrument }}</span>
                      </div>
                      <div v-if="articleDetails.decision_reference" class="eccomp-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.decisionReference') }}</strong>
                        <span>{{ articleDetails.decision_reference }}</span>
                      </div>
                      <div v-if="articleDetails.url || (articleDetails.sourceData && articleDetails.sourceData.link)" class="source-url">
                        <a :href="articleDetails.url || articleDetails.sourceData.link" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                          <strong>{{ $t('modals.legalAnalysis.links.viewEccompSite') }}</strong>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                    </div>

                    <div v-if="articleDetails.source === 'upc'" class="ref-content">
                      <div v-if="articleDetails.case_numbers && articleDetails.case_numbers.length" class="upc-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.caseNumber') }}</strong>
                        <span>{{ articleDetails.case_numbers.join(', ') }}</span>
                      </div>
                      <div v-if="articleDetails.court" class="upc-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.court') }}</strong>
                        <span>{{ articleDetails.court }}</span>
                      </div>
                      <div v-if="articleDetails.action_type" class="upc-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.actionType') }}</strong>
                        <span>{{ articleDetails.action_type }}</span>
                      </div>
                      <div v-if="articleDetails.detail_url || (articleDetails.sourceData && articleDetails.sourceData.detail_url)" class="source-url">
                        <a :href="articleDetails.detail_url || articleDetails.sourceData.detail_url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                          <strong>{{ $t('modals.legalAnalysis.links.viewUpcSite') }}</strong>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                      <div v-if="articleDetails.pdf_url || (articleDetails.sourceData && articleDetails.sourceData.pdf_url)" class="source-url">
                        <a :href="articleDetails.pdf_url || articleDetails.sourceData.pdf_url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                          <strong>{{ $t('modals.legalAnalysis.links.viewUpcPdf') }}</strong>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                    </div>

                    <div v-if="articleDetails.source === 'edpb'" class="ref-content">
                      <div v-if="articleDetails.publication_types && articleDetails.publication_types.length" class="edpb-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.publicationType') }}</strong>
                        <span>{{ articleDetails.publication_types.join(', ') }}</span>
                      </div>
                      <div v-if="articleDetails.topics && articleDetails.topics.length" class="edpb-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.topics') }}</strong>
                        <span>{{ articleDetails.topics.join(', ') }}</span>
                      </div>
                      <div v-if="articleDetails.date" class="edpb-ref-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                        <span>{{ articleDetails.date }}</span>
                      </div>
                      <div v-if="articleDetails.url" class="source-url">
                        <a :href="articleDetails.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                          <strong>{{ $t('modals.legalAnalysis.links.viewEdpbSite') }}</strong>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                      <div v-if="articleDetails.pdf_url_used" class="source-url">
                        <a :href="articleDetails.pdf_url_used" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                          <strong>{{ $t('modals.legalAnalysis.links.viewEdpbPdf') }}</strong>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                    </div>

                <div v-if="articleDetails.source === 'inpi'" class="ref-content">
                  <div v-if="articleDetails.reference" class="inpi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.reference') }}</strong>
                    <span class="inpi-reference">{{ articleDetails.reference }}</span>
                  </div>
                  <div v-if="articleDetails.url_document_final" class="source-url">
                    <a :href="articleDetails.url_document_final" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewInpiSite') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;">
                        <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'bodacc'" class="ref-content">
                  <div v-if="articleDetails.url_complete" class="source-url">
                    <a :href="articleDetails.url_complete" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewBodaccSite') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'reglements'" class="ref-content">
                  <div v-if="articleDetails.celex" class="reglements-info-item">
                    <strong>CELEX:</strong>
                    <a :href="`https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX:${articleDetails.celex}`"
                      target="_blank" rel="noopener noreferrer"
                      style="display: flex; align-items: center; color: #625CFB;">
                      {{ $t('modals.legalAnalysis.links.viewEurLex') }}
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;">
                        <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'actes_societes'" class="ref-content">
                  <div v-if="articleDetails.siren" class="actes-societes-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.siren') }}</strong>
                    <span class="actes-societes-siren-value">{{ articleDetails.siren }}</span>
                  </div>
                  <div v-if="articleDetails.date_acte" class="actes-societes-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.acteDate') }}</strong>
                    <span class="actes-societes-date-full">{{ formatActesSocietesDate(articleDetails.date_acte) }}</span>
                  </div>
                  <div v-if="getActesSocietesSourcePath()" class="source-url">
                    <a :href="getActesSocietesFullUrl(getActesSocietesSourcePath())" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewActePdf') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;">
                        <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/>
                      </svg>
                    </a>
                  </div>
                </div>
                <div v-if="articleDetails.source === 'juritext'" class="ref-content">
                  <div class="decisions-links">
                    <a
                      v-if="articleDetails.texte"
                      href="#"
                      @click.prevent="openTextModal(articleDetails.texte, articleDetails.id, articleDetails.title)"
                    >
                      {{ $t('modals.legalAnalysis.links.viewFullDecision') }}
                    </a>
                    <a :href="getOfficialDecisionLink()" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">{{ $t('modals.legalAnalysis.links.viewOfficialSite') }}
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'convbi'" class="ref-content">
                  <div v-if="articleDetails.pdf_url" class="source-url">
                    <a :href="articleDetails.pdf_url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewConventionPdf') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'bofip'" class="ref-content">
                  <div v-if="articleDetails.sourceData && articleDetails.sourceData.identifiant" class="balo-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.identifier') }}</strong>
                    <span class="balo-siren">{{ articleDetails.sourceData.identifiant }}</span>
                  </div>

                  <div v-if="articleDetails.url" class="source-url">
                    <a :href="articleDetails.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewOfficialSite') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'cnn'" class="ref-content">
                  <div v-if="articleDetails.article_id" class="cnn-reference">
                    <strong>{{ $t('modals.legalAnalysis.labels.reference') }}</strong>
                    <a :href="`https://www.legifrance.gouv.fr/conv_coll/article/${articleDetails.article_id}`" target="_blank" rel="noopener noreferrer" class="legifrance-link" style="display: flex; align-items: center; color: #625CFB;">
                      {{ $t('modals.legalAnalysis.links.legifrance') }}
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'balo'" class="ref-content">
                  <div v-if="articleDetails.id_annonce" class="balo-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.reference') }}</strong>
                    <a :href="getBaloOfficialLink(articleDetails.id_annonce)" target="_blank" rel="noopener noreferrer" class="balo-link" style="display: flex; align-items: center; color: #625CFB;">
                      {{ $t('modals.legalAnalysis.links.viewBaloOfficial') }}
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'euritext'" class="ref-content">
                  <div v-if="articleDetails.sourceData && articleDetails.sourceData.links">
                    <div class="euritext-links">
                      <strong>{{ $t('modals.legalAnalysis.labels.relatedDocuments') }}</strong>
                      <div v-for="(url, title, index) in articleDetails.sourceData.links" :key="index" class="euritext-link">
                        <a :href="url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                          {{ formatEuritextTitle(title) }}
                          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'statuts'" class="ref-content">
                  <div v-if="articleDetails.slugged_title" class="statuts-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.document') }}</strong>
                    <a :href="`https://decisions.query-juriste.com/${articleDetails.slugged_title}`" target="_blank" rel="noopener noreferrer" class="statuts-link" style="display: flex; align-items: center; color: #625CFB;">
                      {{ $t('modals.legalAnalysis.links.viewFullDocument') }}
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a>
                  </div>
                </div>

                <div v-if="articleDetails.source === 'mention'" class="ref-content">
                  <div v-if="articleDetails.original_reference" class="mention-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.originalReference') }}</strong>
                    <span>{{ articleDetails.original_reference }}</span>
                  </div>
                  <div v-if="articleDetails.url" class="source-url">
                    <a :href="articleDetails.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; color: #625CFB;">
                      <strong>{{ $t('modals.legalAnalysis.links.viewOfficialDocument') }}</strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;">
                        <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              <!-- Section générique pour tous les liens officiels -->
              <div v-if="articleDetails.official_link" class="ref-content official-link-section">
                <div class="official-link-item">
                  <a :href="articleDetails.official_link" target="_blank" rel="noopener noreferrer" class="official-link-button">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style="margin-right: 6px;"><path fill-rule="evenodd" d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5"/><path fill-rule="evenodd" d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z"/></svg>
                    {{ $t('modals.legalAnalysis.links.viewOfficialSite') }}
                  </a>
                </div>
              </div>

              <div class="citations-section" v-if="articleDetails.sourceData && Object.keys(articleDetails.sourceData.citations || {}).length > 0">
                <h4 class="citations-title">{{ $t('modals.legalAnalysis.sections.relevantCitations') }}</h4>
                <div class="citation-list">
                  <div v-for="(citation, theme) in articleDetails.sourceData.citations" :key="theme" class="citation-item">
                    <strong>{{ theme }}:</strong>
                    <p class="citation-text">"{{ citation }}"</p>
                  </div>
                </div>
              </div>

              <div class="details-content" v-if="articleDetails.source === 'actes_societes'">
                <div v-if="articleDetails.summary" class="actes-societes-summary-section">
                  <h4 class="summary-title">{{ $t('modals.legalAnalysis.sections.acteSummary') }}</h4>
                  <div v-html="formatText(articleDetails.summary, true)" class="summary-content"></div>
                </div>

                <div v-if="articleDetails.content" class="actes-societes-content-section">
                  <h4 class="content-title">{{ $t('modals.legalAnalysis.sections.acteContent') }}</h4>
                  <div v-html="formatText(articleDetails.content, true)"></div>
                </div>
              </div>

              <div class="details-content" v-if="articleDetails.source === 'infofi'">
                <div class="infofi-metadata">
                  <div v-if="articleDetails.nom_societe" class="infofi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.company') }}</strong>
                    <span class="infofi-company-name">{{ articleDetails.nom_societe }}</span>
                  </div>
                  <div v-if="articleDetails['Titre opération']" class="infofi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.operationTitle') }}</strong>
                    <span>{{ articleDetails['Titre opération'] }}</span>
                  </div>
                  <div v-if="articleDetails.date_document" class="infofi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.documentDate') }}</strong>
                    <span class="infofi-doc-date">{{ formatInfoFiDate(articleDetails.date_document) }}</span>
                  </div>
                  <div v-if="articleDetails.description_information" class="infofi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.informationType') }}</strong>
                    <span class="infofi-type">{{ articleDetails.description_information }}</span>
                  </div>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'convbi'">
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'bodacc'">
                <div class="bodacc-metadata">
                  <div v-if="articleDetails.familleavis" class="bodacc-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.noticeFamily') }}</strong>
                    <span class="bodacc-family-badge">{{ formatBodaccFamily(articleDetails.familleavis) }}</span>
                  </div>
                  <div v-if="articleDetails.typeavis" class="bodacc-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.noticeType') }}</strong>
                    <span class="bodacc-type-badge">{{ formatBodaccType(articleDetails.typeavis) }}</span>
                  </div>
                  <div v-if="articleDetails.dateparution" class="bodacc-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.publicationDate') }}</strong>
                    <span class="bodacc-date">{{ formatBodaccDate(articleDetails.dateparution) }}</span>
                  </div>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'reglements'">
                <div class="reglements-metadata">
                  <div v-if="articleDetails.date" class="reglements-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.reglementDate') }}</strong>
                    <span>{{ formatActesDate(articleDetails.date) }}</span> </div>
                  <div v-if="articleDetails.chapter_title" class="reglements-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.chapter') }}</strong>
                    <span>{{ articleDetails.chapter_title }}</span>
                  </div>
                  <div v-if="articleDetails.celex" class="reglements-info-item">
                    <a :href="formatActesEurLexLink(articleDetails.celex)" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;"><strong><u>{{ $t('modals.legalAnalysis.links.viewEurLex') }}</u></strong>
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="bi bi-arrow-up-right-square" viewBox="0 0 16 16" style="margin-left: 3px;"><path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z"/></svg>
                    </a></div>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'balo'">
                <div class="balo-metadata">
                  <div v-if="articleDetails.societes_noms" class="balo-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.companies') }}</strong>
                    <span class="balo-company-name">{{ articleDetails.societes_noms }}</span>
                  </div>
                  <div v-if="articleDetails.siren" class="balo-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.siren') }}</strong>
                    <span class="balo-siren">{{ articleDetails.siren }}</span>
                  </div>
                  <div v-if="articleDetails.dateparution" class="balo-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.publicationDate') }}</strong>
                    <span class="balo-date">{{ formatBaloDate(articleDetails.dateparution) }}</span>
                  </div>
                  <div v-if="articleDetails.parution_numero" class="balo-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.bulletinNumber') }}</strong>
                    <span class="balo-bulletin">{{ articleDetails.parution_numero }}</span>
                  </div>
                  <div v-if="articleDetails.numero_affaire" class="balo-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.caseNumber') }}</strong>
                    <span class="balo-affaire">{{ articleDetails.numero_affaire }}</span>
                  </div>
                   <div v-if="articleDetails.categorie_libelle" class="balo-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.category') }}</strong>
                    <span class="balo-category-badge">{{ formatBaloCategory(articleDetails.categorie_libelle) }}</span>
                  </div>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'legiarti'">
                <div v-if="articleDetails.hierarchy" class="hierarchy">
                  <strong>{{ formatHierarchy(articleDetails.hierarchy) }}</strong>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
                <div v-if="articleDetails.annotation" class="annotation-section">
                  <h4 class="annotation-title">{{ $t('modals.legalAnalysis.sections.jurisprudenceAnnotation') }}</h4>
                  <div v-html="formatText(articleDetails.annotation, true)" class="annotation-content"></div>
                </div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'cnn'">
                <div class="cnn-metadata">
                  <div v-if="articleDetails.etat" class="cnn-status">
                    <strong>{{ $t('modals.legalAnalysis.labels.state') }}</strong>
                    <span :class="getEtatClass(articleDetails.etat)">{{ formatEtat(articleDetails.etat) }}</span>
                  </div>
                  <div v-if="articleDetails.date_debut || articleDetails.date_fin" class="cnn-validity">
                    <strong>{{ $t('modals.legalAnalysis.labels.inForceSince') }}</strong>
                    <span v-if="articleDetails.date_debut">{{ articleDetails.date_debut }}</span>
                  </div>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'concurrence'">
                <div class="concurrence-metadata">
                  <div v-if="articleDetails.type_document" class="concurrence-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.type') }}</strong>
                    <span class="concurrence-type-badge">{{ articleDetails.type_document }}</span>
                  </div>
                  <div v-if="articleDetails.numero_affaire" class="concurrence-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.caseNumber') }}</strong>
                    <span class="concurrence-affaire-num">{{ articleDetails.numero_affaire }}</span>
                  </div>
                  <div v-if="articleDetails.date_decision" class="concurrence-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.decisionDate') }}</strong>
                    <span class="concurrence-date-full">{{ formatConcurrenceDate(articleDetails.date_decision) }}</span>
                  </div>
                  <div v-if="articleDetails.secteur" class="concurrence-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.sector') }}</strong>
                    <span class="concurrence-sector-name">{{ articleDetails.secteur }}</span>
                  </div>
                  <div v-if="articleDetails.parties" class="concurrence-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.parties') }}</strong>
                    <span class="concurrence-parties">{{ articleDetails.parties }}</span>
                  </div>
                  <div v-if="articleDetails.sens_decision" class="concurrence-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.decisionDirection') }}</strong>
                    <span :class="getConcurrenceDecisionClass(articleDetails.sens_decision)">{{ articleDetails.sens_decision }}</span>
                  </div>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'inpi'">
                <div class="inpi-metadata">
                  <div v-if="articleDetails.domaine" class="inpi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.domain') }}</strong>
                    <span :class="['inpi-domain-badge', getInpiDomainClass(articleDetails.domaine)]">{{ articleDetails.domaine }}</span>
                  </div>
                  <div v-if="articleDetails.reference" class="inpi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.reference') }}</strong>
                    <span class="inpi-reference-num">{{ articleDetails.reference }}</span>
                  </div>
                  <div v-if="articleDetails.date" class="inpi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                    <span class="inpi-date-full">{{ formatInpiDate(articleDetails.date) }}</span>
                  </div>
                  <div v-if="articleDetails.juridiction" class="inpi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.jurisdiction') }}</strong>
                    <span class="inpi-jurisdiction-name">{{ articleDetails.juridiction }}</span>
                  </div>
                  <div v-if="articleDetails.parties" class="inpi-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.parties') }}</strong>
                    <span class="inpi-parties">{{ articleDetails.parties }}</span>
                  </div>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'mention'">
                <div class="mention-metadata">
                  <div v-if="articleDetails.type" class="mention-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.type') }}</strong>
                    <span class="mention-type">{{ articleDetails.type }}</span>
                  </div>
                  <div v-if="articleDetails.jurisdiction" class="mention-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.jurisdiction') }}</strong>
                    <span class="mention-jurisdiction">{{ articleDetails.jurisdiction }}</span>
                  </div>
                  <div v-if="articleDetails.date" class="mention-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                    <span class="mention-date">{{ formatMentionDate(articleDetails.date) }}</span>
                  </div>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'cnil'">
                <div class="cnil-metadata">
                  <div v-if="articleDetails.nature" class="cnil-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.nature') }}</strong>
                    <span class="cnil-nature">{{ articleDetails.nature }}</span>
                  </div>
                  <div v-if="articleDetails.date" class="cnil-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                    <span>{{ formatCnilDate(articleDetails.date) }}</span>
                  </div>
                  <div v-if="articleDetails.titre_complet" class="cnil-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.fullTitle') }}</strong>
                    <span class="cnil-full-title">{{ articleDetails.titre_complet }}</span>
                  </div>
                </div>
                <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.cnilSummary') }}</h4>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else-if="articleDetails.source === 'boss'">
                <div class="boss-metadata">
                  <div v-if="articleDetails.date" class="boss-info-item">
                    <strong>{{ $t('modals.legalAnalysis.labels.updateDate') }}</strong>
                    <span>{{ articleDetails.date ? new Date(articleDetails.date).getFullYear() : '' }}</span>
                  </div>
                </div>
                <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.bossContent') }}</h4>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

                  <div class="details-content" v-else-if="articleDetails.source === 'acpr'">
                    <div class="acpr-metadata">
                      <div v-if="articleDetails.acpr_source" class="acpr-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.source') }}</strong>
                        <span class="acpr-source-badge">{{ articleDetails.acpr_source }}</span>
                      </div>
                      <div v-if="articleDetails.doc_subtype" class="acpr-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.type') }}</strong>
                        <span>{{ articleDetails.doc_subtype }}</span>
                      </div>
                      <div v-if="articleDetails.date" class="acpr-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                        <span>{{ formatAcprDate(articleDetails.date) }}</span>
                      </div>
                      <div v-if="articleDetails.categorie" class="acpr-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.category') }}</strong>
                        <span>{{ articleDetails.categorie }}</span>
                      </div>
                      <div v-if="articleDetails.themes" class="acpr-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.themes') }}</strong>
                        <span>{{ articleDetails.themes }}</span>
                      </div>
                    </div>
                    <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.acprSummary') }}</h4>
                    <div v-html="formattedArticleContent"></div>
                  </div>

                  <div class="details-content" v-else-if="articleDetails.source === 'amf'">
                    <div class="amf-metadata">
                      <div v-if="articleDetails.amf_source" class="amf-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.source') }}</strong>
                        <span class="amf-source-badge">{{ articleDetails.amf_source }}</span>
                      </div>
                      <div v-if="articleDetails.doc_type" class="amf-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.type') }}</strong>
                        <span>{{ articleDetails.doc_type }}</span>
                      </div>
                      <div v-if="articleDetails.reference" class="amf-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.reference') }}</strong>
                        <span>{{ articleDetails.reference }}</span>
                      </div>
                      <div v-if="articleDetails.date" class="amf-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                        <span>{{ articleDetails.date }}</span>
                      </div>
                      <div v-if="articleDetails.themes" class="amf-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.themes') }}</strong>
                        <span>{{ articleDetails.themes }}</span>
                      </div>
                      <div v-if="articleDetails.montant" class="amf-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.amount') }}</strong>
                        <span>{{ articleDetails.montant }}</span>
                      </div>
                      <div v-if="articleDetails.sanctioned_entity" class="amf-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.sanctionedEntity') }}</strong>
                        <span>{{ articleDetails.sanctioned_entity }}</span>
                      </div>
                    </div>
                    <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.amfSummary') }}</h4>
                    <div v-html="formattedArticleContent"></div>
                  </div>

                  <div class="details-content" v-else-if="articleDetails.source === 'eccomp'">
                    <div class="eccomp-metadata">
                      <div v-if="articleDetails.instrument" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.instrument') }}</strong>
                        <span class="eccomp-instrument-badge">{{ articleDetails.instrument }}</span>
                      </div>
                      <div v-if="articleDetails.case_number" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.caseNumber') }}</strong>
                        <span>{{ articleDetails.case_number }}</span>
                      </div>
                      <div v-if="articleDetails.decision_type" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.decisionType') }}</strong>
                        <span>{{ articleDetails.decision_type }}</span>
                      </div>
                      <div v-if="articleDetails.decision_language" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.decisionLanguage') }}</strong>
                        <span>{{ articleDetails.decision_language }}</span>
                      </div>
                      <div v-if="articleDetails.date" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                        <span>{{ articleDetails.date }}</span>
                      </div>
                      <div v-if="articleDetails.member_state" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.memberState') }}</strong>
                        <span>{{ articleDetails.member_state }}</span>
                      </div>
                      <div v-if="articleDetails.companies" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.parties') }}</strong>
                        <span>{{ articleDetails.companies }}</span>
                      </div>
                      <div v-if="articleDetails.legal_basis" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.legalBasis') }}</strong>
                        <span>{{ articleDetails.legal_basis }}</span>
                      </div>
                      <div v-if="articleDetails.sectors" class="eccomp-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.sectors') }}</strong>
                        <span>{{ articleDetails.sectors }}</span>
                      </div>
                    </div>
                    <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.eccompSummary') }}</h4>
                    <div v-html="formattedArticleContent"></div>
                  </div>

                  <div class="details-content" v-else-if="articleDetails.source === 'upc'">
                    <div class="upc-metadata">
                      <div v-if="articleDetails.court" class="upc-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.court') }}</strong>
                        <span class="upc-court-badge">{{ articleDetails.court }}</span>
                      </div>
                      <div v-if="articleDetails.action_type" class="upc-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.actionType') }}</strong>
                        <span>{{ articleDetails.action_type }}</span>
                      </div>
                      <div v-if="articleDetails.case_numbers && articleDetails.case_numbers.length" class="upc-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.caseNumber') }}</strong>
                        <span>{{ articleDetails.case_numbers.join(', ') }}</span>
                      </div>
                      <div v-if="articleDetails.registry_number" class="upc-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.registryNumber') }}</strong>
                        <span>{{ articleDetails.registry_number }}</span>
                      </div>
                      <div v-if="articleDetails.date" class="upc-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                        <span>{{ articleDetails.date }}</span>
                      </div>
                      <div v-if="articleDetails.parties" class="upc-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.parties') }}</strong>
                        <span>{{ articleDetails.parties }}</span>
                      </div>
                      <div v-if="articleDetails.language_of_proceedings" class="upc-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.languageOfProceedings') }}</strong>
                        <span>{{ articleDetails.language_of_proceedings }}</span>
                      </div>
                      <div v-if="articleDetails.document_language" class="upc-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.documentLanguage') }}</strong>
                        <span>{{ articleDetails.document_language }}</span>
                      </div>
                    </div>
                    <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.upcSummary') }}</h4>
                    <div v-html="formattedArticleContent"></div>
                    <div v-if="articleDetails.headnotes" class="upc-headnotes">
                      <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.upcHeadnotes') }}</h4>
                      <div v-html="formatText(articleDetails.headnotes, true)"></div>
                    </div>
                    <div v-if="articleDetails.keywords" class="upc-keywords">
                      <strong>{{ $t('modals.legalAnalysis.labels.keywords') }}</strong>
                      <span>{{ articleDetails.keywords }}</span>
                    </div>
                  </div>

                  <div class="details-content" v-else-if="articleDetails.source === 'edpb'">
                    <div class="edpb-metadata">
                      <div v-if="articleDetails.publication_types && articleDetails.publication_types.length" class="edpb-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.publicationType') }}</strong>
                        <span class="edpb-type-badge">{{ articleDetails.publication_types.join(', ') }}</span>
                      </div>
                      <div v-if="articleDetails.topics && articleDetails.topics.length" class="edpb-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.topics') }}</strong>
                        <span>{{ articleDetails.topics.join(', ') }}</span>
                      </div>
                      <div v-if="articleDetails.members && articleDetails.members.length" class="edpb-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.members') }}</strong>
                        <span>{{ articleDetails.members.join(', ') }}</span>
                      </div>
                      <div v-if="articleDetails.date" class="edpb-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.date') }}</strong>
                        <span>{{ articleDetails.date }}</span>
                      </div>
                      <div v-if="articleDetails.status" class="edpb-info-item">
                        <strong>{{ $t('modals.legalAnalysis.labels.status') }}</strong>
                        <span>{{ articleDetails.status }}</span>
                      </div>
                    </div>
                    <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.edpbSummary') }}</h4>
                    <div v-html="formattedArticleContent"></div>
                    <div v-if="articleDetails.key_points" class="edpb-key-points">
                      <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.edpbKeyPoints') }}</h4>
                      <div v-html="formatText(articleDetails.key_points, true)"></div>
                    </div>
                    <div v-if="articleDetails.keywords" class="edpb-keywords">
                      <strong>{{ $t('modals.legalAnalysis.labels.keywords') }}</strong>
                      <span>{{ articleDetails.keywords }}</span>
                    </div>
                  </div>

              <div class="details-content" v-else-if="articleDetails.source === 'juritext'">
                <div class="juritext-summary-header">
                  <h4 class="summary-section-title">{{ $t('modals.legalAnalysis.sections.juritextSummary') }}</h4>
                  <button
                    v-if="articleDetails.content"
                    class="export-word-button-inline"
                    @click="exportJurisprudenceToWord({ summary: articleDetails.content, title: articleDetails.title }, 0)"
                    :title="$t('modals.legalAnalysis.actions.exportSummaryTooltip')"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-file-earmark-word" viewBox="0 0 16 16">
                      <path d="M5.485 6.879a.5.5 0 1 0-.97.242l1.5 6a.5.5 0 0 0 .967.01L8 9.402l1.018 3.73a.5.5 0 0 0 .967-.01l1.5-6a.5.5 0 0 0-.97-.242l-1.036 4.144-.997-3.655a.5.5 0 0 0-.964 0l-.997 3.655L5.485 6.88z"/>
                      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                    </svg>
                    {{ $t('modals.legalAnalysis.actions.exportSummary') }}
                  </button>
                </div>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>

              <div class="details-content" v-else>
                <div v-html="formatText(articleDetails.content, true)"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <TextModal
      ref="textModal"
      :isVisible="showTextModal"
      :title="modalTitle"
      :text="modalText"
      :citation="modalCitation"
      @close="closeTextModal"
    />
  </div>
</template>

<script>
import Fuse from 'fuse.js';
import DOMPurify from 'dompurify';
import { Document, Paragraph, HeadingLevel, Packer } from 'docx';
import { saveAs } from 'file-saver';

export default {
  name: 'LegalAnalysisModal',
  components: {
    TextModal: () => import('./TextModal.vue')
  },
  props: {
    pointData: {
      type: Object,
      required: true
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    initialDoc: { // <-- ADD THIS ENTIRE PROP DEFINITION
      type: Object,
      default: null
    },
    isFlashMode: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      showModal: false,
      activeTab: 'response',
      showArticlePanel: false,
      isBookmarked: false,
      showTextModal: false,
      modalText: '',
      modalTitle: '',
      modalCitation: '',
      // tabs is now computed (see computed.tabs) so labels re-translate on locale change
      articleDetails: {
        title: '',
        content: '',
        source: '',
        hierarchy: '',
        id: '',
        texte: '',
        url: '',
        sourceData: null // This will now hold the full doc object including 'citations'
      },
      sourceLookup: {},
      indexLookup: {},
      extractedKeywords: [] // Pour stocker les mots-clés extraits
    };
  },
  computed: {
    tabs() {
      return [
        { id: 'response', label: this.$t('modals.legalAnalysis.tabs.response') },
        { id: 'actes_societes', label: this.$t('modals.legalAnalysis.tabs.actesSocietes') },
        { id: 'legiarti', label: this.$t('modals.legalAnalysis.tabs.legiarti') },
        { id: 'juritext', label: this.$t('modals.legalAnalysis.tabs.juritext') },
        { id: 'bofip', label: this.$t('modals.legalAnalysis.tabs.bofip') },
        { id: 'euritext', label: this.$t('modals.legalAnalysis.tabs.euritext') },
        { id: 'reglements', label: this.$t('modals.legalAnalysis.tabs.reglements') },
        { id: 'cnn', label: this.$t('modals.legalAnalysis.tabs.cnn') },
        { id: 'bodacc', label: this.$t('modals.legalAnalysis.tabs.bodacc') },
        { id: 'balo', label: this.$t('modals.legalAnalysis.tabs.balo') },
        { id: 'statuts', label: this.$t('modals.legalAnalysis.tabs.statuts') },
        { id: 'convbi', label: this.$t('modals.legalAnalysis.tabs.convbi') },
        { id: 'infofi', label: this.$t('modals.legalAnalysis.tabs.infofi') },
        { id: 'energy', label: this.$t('modals.legalAnalysis.tabs.energy') },
        { id: 'concurrence', label: this.$t('modals.legalAnalysis.tabs.concurrence') },
        { id: 'inpi', label: this.$t('modals.legalAnalysis.tabs.inpi') },
        { id: 'mention', label: this.$t('modals.legalAnalysis.tabs.mention') },
        { id: 'cnil', label: this.$t('modals.legalAnalysis.tabs.cnil') },
        { id: 'boss', label: this.$t('modals.legalAnalysis.tabs.boss') },
        { id: 'acpr', label: this.$t('modals.legalAnalysis.tabs.acpr') },
        { id: 'amf', label: this.$t('modals.legalAnalysis.tabs.amf') },
        { id: 'eccomp', label: this.$t('modals.legalAnalysis.tabs.eccomp') },
        { id: 'upc', label: this.$t('modals.legalAnalysis.tabs.upc') },
        { id: 'edpb', label: this.$t('modals.legalAnalysis.tabs.edpb') },
        { id: 'agcm', label: this.$t('modals.legalAnalysis.tabs.agcm') },
        { id: 'giustizia_amministrativa', label: this.$t('modals.legalAnalysis.tabs.giustiziaAmministrativa') },
        { id: 'jurisprudence_europe_italy', label: this.$t('modals.legalAnalysis.tabs.jurisprudenceEuropeItaly') },
        { id: 'eurlex_italy', label: this.$t('modals.legalAnalysis.tabs.eurlexItaly') },
      ];
    },

    sortedJuritextSources() {
      const sources = this.getSourceItems('juritext');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedLegiartiSources() {
      const sources = this.getSourceItems('legiarti');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedBofipSources() {
      const sources = this.getSourceItems('bofip');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedEuritextSources() {
      const sources = this.getSourceItems('euritext');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedCnnSources() {
      const sources = this.getSourceItems('cnn');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedBaloSources() {
      const sources = this.getSourceItems('balo');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedStatutsSources() {
      const sources = this.getSourceItems('statuts');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedConvbiSources() {
      const sources = this.getSourceItems('convbi');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedInfofiSources() {
      const sources = this.getSourceItems('infofi');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedReglementsSources() {
      const sources = this.getSourceItems('reglements');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedBodaccSources() {
      const sources = this.getSourceItems('bodacc');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedEnergySources() {
      const sources = this.getSourceItems('energy');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedConcurrenceSources() {
      const sources = this.getSourceItems('concurrence');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedInpiSources() {
      const sources = this.getSourceItems('inpi');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },
    sortedCnilSources() {
      const sources = this.getSourceItems('cnil');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },
    sortedBossSources() {
      const sources = this.getSourceItems('boss');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },
      sortedAcprSources() {
        const sources = this.getSourceItems('acpr');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },
      sortedAmfSources() {
        const sources = this.getSourceItems('amf');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },

      sortedEccompSources() {
        const sources = this.getSourceItems('eccomp');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },

      sortedUpcSources() {
        const sources = this.getSourceItems('upc');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },

      sortedEdpbSources() {
        const sources = this.getSourceItems('edpb');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },

      sortedAgcmSources() {
        const sources = this.getSourceItems('agcm');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },

      sortedGiustiziaAmministrativaSources() {
        const sources = this.getSourceItems('giustizia_amministrativa');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },

      sortedJurisprudenceEuropeItalySources() {
        const sources = this.getSourceItems('jurisprudence_europe_italy');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },

      sortedEurlexItalySources() {
        const sources = this.getSourceItems('eurlex_italy');
        return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
      },

    sortedMentionSources() {
      const sources = this.getSourceItems('mention');
      return sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));
    },

    sortedActesSocietesSources() {
      const sources = this.getSourceItems('actes_societes');
      const sorted = sources.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));

      // Remove duplicates based on the PDF URL
      const seen = new Set();
      return sorted.filter(doc => {
        // Utiliser source_path en priorité (mode FLASH), sinon source (mode normal)
        // Mais ignorer si source === 'actes_societes' (c'est juste le nom de la source, pas une URL)
        let sourceUrl = doc.source_path;
        if (!sourceUrl && doc.source && doc.source !== 'actes_societes') {
          sourceUrl = doc.source;
        }

        // Si pas d'URL, utiliser un identifiant alternatif (acte_id, siren+date, ou index)
        if (!sourceUrl) {
          sourceUrl = doc.acte_id || `${doc.siren}-${doc.date_acte}` || doc.id_answer;
        }

        if (sourceUrl && seen.has(sourceUrl)) {
          return false; // Skip duplicate
        }
        if (sourceUrl) {
          seen.add(sourceUrl);
        }
        return true;
      });
    },

    availableTabs() {
      // Déterminer les onglets de base
      const baseTabs = this.isFlashMode
        ? [] // En mode FLASH, on ne commence PAS par l'onglet "Réponse"
        : [this.tabs[0]]; // En mode normal, on commence par "Réponse"

      const sources = ['legiarti', 'juritext', 'bofip', 'euritext', 'reglements', 'cnn', 'balo', 'bodacc', 'statuts', 'convbi', 'infofi', 'energy', 'actes_societes', 'concurrence', 'inpi', 'mention', 'cnil', 'boss', 'acpr', 'amf', 'eccomp', 'upc', 'edpb', 'agcm', 'giustizia_amministrativa', 'jurisprudence_europe_italy', 'eurlex_italy'];

      sources.forEach(source => {
        const sourceItems = this.getSourceItems(source);
        if (sourceItems.length > 0) {
          const tab = this.tabs.find(t => t.id === source);
          if (tab) {
            baseTabs.push(tab);
          }
        }
      });

      return baseTabs;
    },
  },
  watch: {
    isVisible(val) {
      this.showModal = val;
      this.toggleBodyScroll(!val);

      // LOGIQUE MISE À JOUR :
      if (val) {
        // Gérer la logique d'ouverture chaque fois que le modal devient visible
        this.$nextTick(() => this.handleModalOpen());
      }
    },

    pointData(newVal) {
      if (newVal) {
        // Cette logique est importante pour réinitialiser le modal
        // si les données changent
        this.showArticlePanel = false;
        this.buildSourceIndexes();

        if (newVal.point) {
          this.extractedKeywords = this.extractKeywords(newVal.point);
        }

        // Réglage de l'onglet par défaut (uniquement si on n'ouvre pas un doc spécifique)
        if (!this.initialDoc) {
          if (this.isFlashMode) {
              this.$nextTick(() => {
                this.activeTab = this.availableTabs[0]?.id || 'legiarti';
              });
          } else {
              this.activeTab = 'response';
          }
        }
      }
    },

    showModal(val) {
      this.toggleBodyScroll(!val);
    },

    availableTabs(newTabs) {
      if (!newTabs.some(tab => tab.id === this.activeTab)) {
        this.activeTab = 'response';
      }
    },
    showArticlePanel(isVisible) {
      if (isVisible) {
        // Appliquer le surlignage uniquement lorsque le panneau est visible
        this.$nextTick(() => {
          this.applyKeywordHighlighting();
        });
      }
    }
  },

  // DANS LegalAnalysisModal.vue -> mounted()

  mounted() {
    this.showModal = this.isVisible;
    if (this.showModal) {
      this.toggleBodyScroll(false);
    }
    this.buildSourceIndexes();

    // LOGIQUE MISE À JOUR :
    if (this.isVisible) {
      // $nextTick s'assure que tout est rendu avant de tenter
      // de trouver des documents ou de changer d'onglet
      this.$nextTick(() => this.handleModalOpen());
    }

    if (this.pointData && this.pointData.point) {
      this.extractedKeywords = this.extractKeywords(this.pointData.point);
    }
  },

  beforeDestroy() {
    if (this.showModal) {
      this.toggleBodyScroll(true);
    }
  },
  methods: {
    // DANS LegalAnalysisModal.vue -> methods:

    extractFollowUpQuestion(content) {
      // Vérifier si c'est une question de suivi
      if (content.includes('Question de suivi:') && content.includes('Contexte:')) {
        // Extraire seulement la partie après "Question de suivi:"
        const parts = content.split('Question de suivi:');
        if (parts.length > 1) {
          return parts[1].trim();
        }
      }
      // Sinon retourner le contenu complet
      return content;
    },

    async exportJurisprudenceToWord(doc, docIndex) {
      if (!doc.summary) {
        return;
      }

      try {
        const title = doc.title || this.$t('modals.legalAnalysis.fallbacks.decision', { n: docIndex + 1 });

        // Extraire seulement la question de suivi si c'est le cas
        const contentToExport = this.extractFollowUpQuestion(doc.summary);

        // Parse le summary en markdown simple et convertit en paragraphes Word
        const paragraphs = this.parseSummaryToDocx(contentToExport, title);

        // Créer le document Word
        const wordDoc = new Document({
          // Sans ce flag, Word étire sur toute la largeur les lignes justifiées
          // terminées par un saut de ligne manuel (<br> -> TextRun break:1)
          compatibility: { doNotExpandShiftReturn: true },
          sections: [{
            properties: {},
            children: paragraphs
          }]
        });

        // Générer le fichier
        const blob = await Packer.toBlob(wordDoc);

        // Créer un nom de fichier sanitizé
        const sanitizedTitle = title.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
        const filename = `${sanitizedTitle}_summary.docx`;

        // Télécharger
        saveAs(blob, filename);
      } catch (error) {
        console.error('Erreur lors de l\'export Word:', error);
        alert(this.$t('modals.legalAnalysis.toasts.wordExportError'));
      }
    },

    parseSummaryToDocx(summary, title) {
      const paragraphs = [];

      // Ajouter le titre
      paragraphs.push(
        new Paragraph({
          text: title,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 }
        })
      );

      // Ajouter un sous-titre
      paragraphs.push(
        new Paragraph({
          text: this.$t('modals.legalAnalysis.docx.decisionSummary'),
          heading: HeadingLevel.HEADING_2,
          spacing: { after: 200 }
        })
      );

      // Parser le contenu du summary
      const lines = summary.split('\n');

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        if (!line) {
          // Ligne vide
          paragraphs.push(new Paragraph({ text: '' }));
          continue;
        }

        // Détecter les titres markdown (## ou ###)
        if (line.startsWith('### ')) {
          paragraphs.push(
            new Paragraph({
              text: line.substring(4),
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 }
            })
          );
        } else if (line.startsWith('## ')) {
          paragraphs.push(
            new Paragraph({
              text: line.substring(3),
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            })
          );
        } else if (line.startsWith('# ')) {
          paragraphs.push(
            new Paragraph({
              text: line.substring(2),
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 200, after: 100 }
            })
          );
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          // Liste à puces
          paragraphs.push(
            new Paragraph({
              text: line.substring(2),
              bullet: { level: 0 },
              spacing: { after: 100 }
            })
          );
        } else if (line.match(/^\d+\.\s/)) {
          // Liste numérotée
          const text = line.replace(/^\d+\.\s/, '');
          paragraphs.push(
            new Paragraph({
              text: text,
              numbering: { reference: 'default-numbering', level: 0 },
              spacing: { after: 100 }
            })
          );
        } else {
          // Paragraphe normal
          // Nettoyer les balises HTML simples si présentes
          const cleanText = line
            .replace(/<\/?strong>/g, '')
            .replace(/<\/?em>/g, '')
            .replace(/<\/?b>/g, '')
            .replace(/<\/?i>/g, '');

          paragraphs.push(
            new Paragraph({
              text: cleanText,
              spacing: { after: 100 }
            })
          );
        }
      }

      return paragraphs;
    },

    // 1. VOTRE FONCTION (pour le mode Normal)
    // Renommée pour plus de clarté, mais vous pouvez garder openInitialDocument si vous préférez
    highlightInitialDocumentReference() {
      if (!this.initialDoc) return;

      const { sourceKey, index } = this.initialDoc;

      // 1. S'assurer que l'onglet 'Réponse' est actif
      this.activeTab = 'response';

      this.$nextTick(() => {
        const responseContainer = this.$refs.responseContent;
        if (!responseContainer) return;

        // 2. Trouver l'élément cible
        const targetElement = responseContainer.querySelector(
          `a.inline-reference[data-source="${sourceKey}"][data-index="${index}"]`
        );

        if (targetElement) {
          // 3. Scroller jusqu'à lui
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // 4. Appliquer le surlignage
          const originalInlineStyle = targetElement.style.cssText;
          targetElement.style.backgroundColor = '#fff3cd'; // Jaune clair
          targetElement.style.boxShadow = '0 0 0 2px #ffeeba';
          targetElement.style.borderRadius = '4px';
          targetElement.style.transition = 'background-color 0.5s ease, box-shadow 0.5s ease';

          // 5. Retirer le surlignage après 3.5 secondes
          setTimeout(() => {
            targetElement.style.cssText = originalInlineStyle;
          }, 3500);
        }
      });
    },

    getActesSocietesSourcePath() {
      // Vérifier d'abord source_path (prioritaire)
      if (this.articleDetails.source_path) {
        return this.articleDetails.source_path;
      }

      // Vérifier dans sourceData
      if (this.articleDetails.sourceData) {
        // Mode FLASH: source_path existe
        if (this.articleDetails.sourceData.source_path) {
          return this.articleDetails.sourceData.source_path;
        }
        // Mode normal: source contient l'URL (pas le nom de la source)
        if (this.articleDetails.sourceData.source &&
            this.articleDetails.sourceData.source !== 'actes_societes') {
          return this.articleDetails.sourceData.source;
        }
      }

      return null;
    },

    // 2. NOUVELLE FONCTION (pour le mode FLASH)
    // Ouvre directement le panneau de détails
    openInitialDocDetails() {
      if (!this.initialDoc) return;
      const { sourceKey, index } = this.initialDoc;

      const doc = this.getDocumentFromPoint(this.pointData, sourceKey, index);

      if (doc) {
        const details = this.processDocumentBySource(doc, sourceKey, index);
        // Ouvre le panneau de droite
        this.showArticleDetails(details);

        // Change l'onglet actif pour celui de la source
        if (this.availableTabs.some(t => t.id === sourceKey)) {
          this.activeTab = sourceKey;
        }
      } else {
        console.error("Mode FLASH: Impossible de trouver le document initial:", this.initialDoc);
      }
    },

    // 3. NOUVELLE FONCTION (Aiguilleur)
    // Gère l'ouverture du modal
    handleModalOpen() {
      // Cas 1: On clique sur une référence (un `initialDoc` est fourni)
      if (this.initialDoc) {
        if (this.isFlashMode) {
          // Mode FLASH : Ouvrir le panneau de droite directement
          this.openInitialDocDetails();
        } else {
          // Mode NORMAL : Surligner la référence dans l'onglet "Réponse"
          this.highlightInitialDocumentReference();
        }
      }
      // Cas 2: On clique sur "Voir les sources" / "Analyse complète" (`initialDoc` est null)
      else {
        this.showArticlePanel = false; // Panneau de droite fermé
        if (this.isFlashMode) {
          // Si des onglets de source existent, aller au premier
          this.activeTab = this.availableTabs[0]?.id || 'legiarti';
        } else {
          // Mode normal, aller à l'onglet "Réponse"
          this.activeTab = 'response';
        }
      }
    },

    getDocumentFromPoint(point, sourceKey, docIndex) {
      if (!point.analyses) return null;

      const sourceBlock = point.analyses.find(block => block.source === sourceKey);
      if (sourceBlock && sourceBlock.analysis && sourceBlock.analysis[docIndex]) {
        return sourceBlock.analysis[docIndex];
      }
      return null;
    },

    getEnergySources() {
      return this.sortedEnergySources;
    },

    getConcurrenceSources() {
      return this.sortedConcurrenceSources;
    },

    getInpiSources() {
      return this.sortedInpiSources;
    },

    getCnilSources() {
      return this.sortedCnilSources;
    },

    getBossSources() {
      return this.sortedBossSources;
    },

      getAcprSources() {
        return this.sortedAcprSources;
      },
      getAmfSources() {
        return this.sortedAmfSources;
      },
      getEccompSources() {
        return this.sortedEccompSources;
      },
      getUpcSources() {
        return this.sortedUpcSources;
      },
      getEdpbSources() {
        return this.sortedEdpbSources;
      },
      getAgcmSources() {
        return this.sortedAgcmSources;
      },
      getGiustiziaAmministrativaSources() {
        return this.sortedGiustiziaAmministrativaSources;
      },
      getJurisprudenceEuropeItalySources() {
        return this.sortedJurisprudenceEuropeItalySources;
      },
      getEurlexItalySources() {
        return this.sortedEurlexItalySources;
      },

    getMentionSources() {
      return this.sortedMentionSources;
    },

    formatMentionDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

    getMentionTitle(doc) {
      if (doc.title) return doc.title;

      // Construction intelligente du titre
      const parts = [];
      if (doc.type) parts.push(doc.type);
      if (doc.jurisdiction) parts.push(doc.jurisdiction);
      if (doc.date) parts.push(this.formatMentionDate(doc.date));

      if (parts.length > 0) return parts.join(' - ');

      return null;
    },

    formatEnergyDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

    formatConcurrenceDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

    getConcurrenceDecisionClass(sens) {
      if (!sens) return 'decision-default';
      const sensLower = sens.toLowerCase();
      if (sensLower.includes('sanction') || sensLower.includes('condamn')) return 'decision-sanction';
      if (sensLower.includes('accept') || sensLower.includes('autoris')) return 'decision-accepted';
      if (sensLower.includes('rejet') || sensLower.includes('refus')) return 'decision-rejected';
      return 'decision-default';
    },

    formatInpiDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

    formatCnilDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

      formatAcprDate(dateString) {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          }
        } catch (e) {
          return dateString;
        }
        return dateString;
      },

    getInpiDomainClass(domaine) {
      if (!domaine) return 'domain-default';
      const domaineLower = domaine.toLowerCase();
      if (domaineLower.includes('marque')) return 'domain-marques';
      if (domaineLower.includes('brevet')) return 'domain-brevets';
      if (domaineLower.includes('dessin') || domaineLower.includes('modèle')) return 'domain-dessins';
      return 'domain-default';
    },

    // ========== Nouvelle méthode pour vérifier s'il y a des références ==========
    hasReferences() {
      const source = this.articleDetails.source;
      switch (source) {
        case 'juritext':
          return this.articleDetails.texte || this.articleDetails.id;
        case 'bofip':
          return this.articleDetails.url;
        case 'cnn':
          return this.articleDetails.article_id;
        case 'balo':
          return this.articleDetails.id_annonce;
        case 'euritext':
          return this.articleDetails.sourceData && this.articleDetails.sourceData.links;
        case 'statuts':
          return this.articleDetails.slugged_title;
        case 'convbi':
          return this.articleDetails.pdf_url;
        case 'infofi':
          return this.articleDetails.sourceData && this.articleDetails.sourceData.Lien_Document;
        case 'bodacc':
          return this.articleDetails.url_complete;
        case 'energy':
          return this.articleDetails.url;
        case 'actes_societes': // ← NOUVEAU
          return this.articleDetails.sourceData && this.articleDetails.sourceData.source;
        case 'concurrence':
          return this.articleDetails.sourceData && this.articleDetails.sourceData.url_page;
        case 'inpi':
          return this.articleDetails.sourceData && this.articleDetails.sourceData.url_document_final;
        case 'cnil':
          return this.articleDetails.url || (this.articleDetails.sourceData && this.articleDetails.sourceData.link);
        case 'boss':
          return this.articleDetails.url || (this.articleDetails.sourceData && (this.articleDetails.sourceData.link || this.articleDetails.sourceData.url));
            case 'acpr':
              return this.articleDetails.url || (this.articleDetails.sourceData && this.articleDetails.sourceData.link);
            case 'amf':
              return this.articleDetails.url || (this.articleDetails.sourceData && this.articleDetails.sourceData.link);
            case 'eccomp':
              return this.articleDetails.url || (this.articleDetails.sourceData && (this.articleDetails.sourceData.link || this.articleDetails.sourceData.url)) || this.articleDetails.case_number;
            case 'upc':
              return this.articleDetails.detail_url || this.articleDetails.pdf_url || this.articleDetails.url || (this.articleDetails.sourceData && (this.articleDetails.sourceData.detail_url || this.articleDetails.sourceData.pdf_url || this.articleDetails.sourceData.url));
            case 'edpb':
              return this.articleDetails.url || this.articleDetails.pdf_url_used || (this.articleDetails.sourceData && (this.articleDetails.sourceData.url || this.articleDetails.sourceData.pdf_url_used));
        case 'mention':
          return this.articleDetails.sourceData && (this.articleDetails.sourceData.url || this.articleDetails.sourceData.original_reference);
        default:
          return false;
      }
    },

    getJuritextSources() {
      return this.sortedJuritextSources;
    },

    getLegiartiSources() {
      return this.sortedLegiartiSources;
    },

    getBofipSources() {
      return this.sortedBofipSources;
    },

    getEuritextSources() {
      return this.sortedEuritextSources;
    },

    getCnnSources() {
      return this.sortedCnnSources;
    },

    getBaloSources() {
      return this.sortedBaloSources;
    },

    getStatutsSources() {
      return this.sortedStatutsSources;
    },

    getConvbiSources() {
      return this.sortedConvbiSources;
    },

    getInfofiSources() {
      return this.sortedInfofiSources;
    },

    getBodaccSources() {
      return this.sortedBodaccSources;
    },

    getActesSocietesSources() {
      return this.sortedActesSocietesSources;
    },

    formatActesSocietesDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

    formatActesSocietesKeywords(keywords) {
      if (!keywords) return '';
      // Limiter à 5 mots-clés maximum pour l'affichage
      const keywordArray = keywords.split(',').slice(0, 5);
      return keywordArray.join(', ');
    },

    formatActesDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

    formatActesEurLexLink(celex) {
      try {
        return `https://eur-lex.europa.eu/legal-content/FR/TXT/PDF/?uri=CELEX:${celex}`
      } catch (e) {
        return this.$t('modals.legalAnalysis.fallbacks.noLinkAvailable');
      }
    },

    formatInfoFiDate(dateString) {
        if (!dateString) return '';
        // Gère les formats comme "4/25/2022 4:15:00 PM"
        try {
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }
        } catch (e) {
            return dateString;
        }
        return dateString;
    },

    formatBaloDate(dateString) {
      if (!dateString) return '';

      // Si c'est une date au format "MM/DD/YYYY HH:MM:SS AM/PM"
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        // Si le format n'est pas reconnu, retourner tel quel
        return dateString;
      }

      return dateString;
    },

    formatBaloCategory(categorie) {
      if (!categorie) return '';

      // Split par "##" et prendre la partie après
      const parts = categorie.split('##');
      if (parts.length > 1) {
        return parts[1].trim();
      }

      return categorie;
    },

    getStatutsTitle(pdfName) {
      if (!pdfName) return '';
      // Enlever l'extension .pdf à la fin
      return pdfName.replace(/\.pdf$/i, '');
    },

    getBaloOfficialLink(idAnnonce, sourceData = null) {
      // Priorité 1: Utiliser le nom du fichier PDF s'il est disponible dans sourceData
      let fname = null;
      if (sourceData && sourceData.sourceData && sourceData.sourceData.fichiers_joints_fichier_pdf_text) {
        fname = sourceData.sourceData.fichiers_joints_fichier_pdf_text;
      } else if (this.articleDetails.sourceData && this.articleDetails.sourceData.fichiers_joints_fichier_pdf_text) {
        fname = this.articleDetails.sourceData.fichiers_joints_fichier_pdf_text;
      }

      // Priorité 2: Construire le nom du fichier à partir de l'ID d'annonce
      if (!fname && idAnnonce) {
        // Supposer que l'ID d'annonce peut être utilisé pour construire le nom de fichier
        // Format attendu:YYYYMMDDHHMMNNN.pdf
        if (idAnnonce.length >= 12) {
          fname = `${idAnnonce}`;
        }
      }

      if (fname) {
        try {
          // Format: https://www.journal-officiel.gouv.fr/telechargements/BALO/pdf/{fname[:4]}/{fname[4:8]}/{fname}
          const year = fname.substring(0, 4);    // 4 premiers caractères
          const monthDay = fname.substring(4, 8); // caractères 4 à 7

          return `https://www.journal-officiel.gouv.fr/telechargements/BALO/pdf/${year}/${monthDay}/${fname}`;
        } catch (e) {
          // Fallback to general page on parse error
        }
      }

      // Fallback vers la page générale Journal Officiel
      return 'https://www.journal-officiel.gouv.fr/pages/balo/';
    },

    // ========== Nouvelle méthode pour formater les titres Euritext ==========
    formatEuritextTitle(title) {
      if (!title) return '';

      // Remplacer "arret" par le label traduit
      if (title.toLowerCase() === 'arret') {
        return this.$t('modals.legalAnalysis.euritextTitles.arret');
      }

      // Pour les autres titres, mettre la première lettre en majuscule
      return title.charAt(0).toUpperCase() + title.slice(1);
    },

    // Dans les méthodes computed

    // Dans les methods
    getReglementsSources() {
      return this.sortedReglementsSources;
    },

    formatReglementsTitle(doc) {
      if (!doc) return '';

      // Créer un titre court à partir du titre du document et de l'article
      let title = '';
      if (doc.article_number) {
        title = doc.article_number;
      }

      // Ajouter une partie du titre du règlement si disponible
      if (doc.doc_title) {
        // Prendre seulement le numéro du règlement
        const regMatch = doc.doc_title.match(/RÈGLEMENT \(UE\) No\s*(\d+\/\d+)/);
        if (regMatch) {
          title += ` - ${this.$t('modals.legalAnalysis.reglements.euShort', { num: regMatch[1] })}`;
        }
      }

      return title || this.$t('modals.legalAnalysis.fallbacks.reglement', { n: doc.index + 1 });
    },

    // ========== Méthodes d'origine ==========
    closeModal() {
      this.showModal = false;
      this.showArticlePanel = false;
      this.$emit('close');
      this.toggleBodyScroll(true);
    },

    changeTab(tabId) {
      this.activeTab = tabId;
      this.showArticlePanel = false;
    },

    toggleBookmark() {
      this.isBookmarked = !this.isBookmarked;
    },

    showArticleDetails(details) {
      this.articleDetails = details;
      this.showArticlePanel = true;
      // Le surlignage sera appliqué via le watcher showArticlePanel
    },

    closeArticleDetails() {
      this.showArticlePanel = false;
    },

    async openTextModal(texte, decisionId, title) {
      this.showTextModal = true;
      this.modalText = texte;
      this.modalTitle = title;

      this.$nextTick(() => {
        if (this.$refs.textModal) {
          this.$refs.textModal.highlightCitation();
        }
      });
    },

    closeTextModal() {
      this.showTextModal = false;
    },

    formatHierarchy(hierarchy) {
      if (!hierarchy) return '';
      return hierarchy
        .split(' > ')
        .map(section => section.trim())
        .join(' > ');
    },

    // ========== Méthodes de surlignage ==========
    // Extraire les termes clés d'une question
    extractKeywords(text) {
      if (!text) return [];

      // Liste des mots vides français
      const stopWords = ['le', 'la', 'les', 'un', 'une', 'des', 'et', 'ou', 'en', 'à', 'de', 'du', 'ce', 'cette',
        'ces', 'est', 'sont', 'pour', 'dans', 'par', 'sur', 'au', 'aux', 'avec', 'qui', 'que', 'quoi', 'dont',
        'où', 'comment', 'quand', 'pourquoi', 'quel', 'quelle', 'quels', 'quelles', 'ne', 'pas', 'plus', 'moins',
        'si', 'je', 'tu', 'il', 'elle', 'nous', 'vous', 'ils', 'elles', 'se', 'me', 'te', 'lui', 'leur', 'y'];

      // Extraction des mots significatifs (au moins 3 caractères)
      const words = text.toLowerCase().match(/\b[a-zàáâäæçèéêëìíîïòóôöùúûüÿœ]{3,}\b/g) || [];
      const filteredWords = words.filter(word => !stopWords.includes(word));

      return [...new Set(filteredWords)]; // Éliminer les doublons
    },

    // Expansion sémantique par thésaurus juridique
    expandKeywords(keywords) {
      // Thésaurus juridique simplifié (à compléter selon vos besoins)
      const legalThesaurus = {
        'licenciement': ['rupture', 'résiliation', 'congédiement', 'révocation', 'destitution'],
        'licéité': ['légalité', 'validité', 'conformité', 'régularité'],
        'travail': ['emploi', 'fonction', 'activité', 'profession'],
        'contrat': ['convention', 'accord', 'engagement', 'pacte'],
        'droit': ['prérogative', 'faculté', 'pouvoir', 'autorisation'],
        'obligation': ['devoir', 'engagement', 'contrainte', 'responsabilité'],
        'salarié': ['employé', 'travailleur', 'collaborateur', 'personnel'],
        'employeur': ['patron', 'entreprise', 'société', 'direction'],
        'indemnité': ['compensation', 'dédommagement', 'réparation', 'allocation'],
        'procédure': ['processus', 'démarche', 'formalité', 'protocole'],
        'impôt': ['taxe', 'contribution', 'prélèvement', 'imposition'],
        'fiscal': ['tributaire', 'imposable', 'contributif']
      };

      return keywords.flatMap(term => {
        // Recherche insensible à la casse et aux accents
        const normalizedTerm = term.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        for (const [key, synonyms] of Object.entries(legalThesaurus)) {
          const normalizedKey = key.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
          if (normalizedKey === normalizedTerm) {
            return [term, ...synonyms];
          }
        }
        return [term];
      });
    },

    // Configuration de la recherche approximative avec Fuse.js
    setupFuzzySearch(content) {
      if (!content) return null;

      const contentChunks = content.split(/\b/);
      return new Fuse(contentChunks, {
        includeScore: true,
        threshold: 0.3, // Seuil de tolérance
        ignoreLocation: true,
        distance: 200,
        minMatchCharLength: 3,
        findAllMatches: true,
        useExtendedSearch: true
      });
    },

    // Surlignage des termes avec gestion des chevauchements
    highlightKeywords(content, keywords) {
      if (!content || !keywords.length) return content;

      // Élargissement de l'ensemble de termes à rechercher
      const expandedKeywords = this.expandKeywords(keywords);

      // Préparation du texte pour le surlignage
      let highlightedContent = content;
      let matches = [];

      // Recherche simple de correspondances exactes
      expandedKeywords.forEach(keyword => {
        const regex = new RegExp(`\\b${this.escapeRegExp(keyword)}\\b`, 'gi');
        let match;
        while ((match = regex.exec(highlightedContent)) !== null) {
          matches.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0]
          });
        }
      });

      // Si aucune correspondance exacte, essayer la recherche approximative
      if (matches.length === 0) {
        const fuse = this.setupFuzzySearch(highlightedContent);
        if (fuse) {
          expandedKeywords.forEach(keyword => {
            const fuzzyMatches = fuse.search(keyword);
            fuzzyMatches.slice(0, 5).forEach(match => { // Limiter aux 5 meilleurs résultats
              if (match.score < 0.3) { // Seuil de confiance
                const idx = match.refIndex;
                const matchedWord = fuse._docs[idx];
                const start = highlightedContent.indexOf(matchedWord);
                if (start >= 0) {
                  const end = start + matchedWord.length;
                  matches.push({
                    start,
                    end,
                    text: matchedWord
                  });
                }
              }
            });
          });
        }
      }

      // Fusion des chevauchements
      matches = this.mergeRanges(matches);

      // Application du surlignage en commençant par la fin pour éviter de décaler les indices
      let result = highlightedContent;
      matches.sort((a, b) => b.start - a.start); // Tri décroissant
      matches.forEach(match => {
        const before = result.substring(0, match.start);
        const term = result.substring(match.start, match.end);
        const after = result.substring(match.end);
        result = before + `<span class="keyword-highlight">${term}</span>` + after;
      });

      return result;
    },

    // Fusion des plages de texte qui se chevauchent
    mergeRanges(matches) {
      if (!matches.length) return [];

      matches.sort((a, b) => a.start - b.start);
      const merged = [matches[0]];

      for (let i = 1; i < matches.length; i++) {
        const current = matches[i];
        const last = merged[merged.length - 1];

        if (current.start <= last.end) {
          // Chevauchement détecté, fusion des plages
          last.end = Math.max(last.end, current.end);
        } else {
          // Pas de chevauchement, ajouter comme nouvelle plage
          merged.push(current);
        }
      }

      return merged;
    },

    // Échapper les caractères spéciaux pour les expressions régulières
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    // Format text avec option de surlignage de termes clés
    formatText(text, withHighlighting = false) {
      if (!text) return '';

      // 1. Échapper le HTML - KEEP THIS LINE, don't escape HTML
      let formattedText = text;

      // Handle *** separator
      formattedText = formattedText.replace(/\n\s*\*\*\*\s*\n/g, '\n\n');

      // Handle Roman numerals (I), II), III), IV), etc.) - NOUVELLE LIGNE
      formattedText = formattedText.replace(
        /^#{0,6}\s*(\*{0,2}([IVX]+)\)\*{0,2}\s*)([^\n]+)/gm,
        "<h3 style='margin-top: 1.5em !important; font-size: 1.3em; font-weight: bold; color: #625CFB;'>$1$3</h3>"
      );

      // Handle markdown headings
      formattedText = formattedText.replace(/####\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/###\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/##\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/#\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');

      // Handle bullet points with indentation levels
      formattedText = formattedText.replace(
        /(^|\n)( *)\*\s+(.*?)(?=\n|$)/g,
        (match, sep, indent, content) => {
          const level = Math.floor(indent.length / 4) + 1;         // 1, 2, 3, …
          let bullet;
          switch (level) {
            case 1: bullet = '-'; break;
            case 2: bullet = '•'; break;
            case 3: bullet = '◦'; break;
            default: bullet = '▪';                                 // niveaux ≥ 4
          }
          return `${sep}<li style="margin-left:${level}em; margin-top:1.2em; margin-bottom:1.2em; padding-left:0.5em;">${bullet} ${content}</li>`;
        }
      );

      // Group consecutive <li> elements in <ul>
      formattedText = formattedText.replace(
        /(<li[\s\S]*?<\/li>)+/g,
        m => `<ul>${m}</ul>`
      );

      // Merge adjacent </ul><ul>
      formattedText = formattedText.replace(/<\/ul>\s*<ul>/g, '');

      // 2. Mettre en italique les citations
      formattedText = formattedText.replace(/"([^"]+)"/g, '"<em>$1</em>"');
      formattedText = formattedText.replace(/«([^»]+)»/g, '«<em>$1</em>»');

      // 3. Traiter les références
      formattedText = this.processReferencesInText(formattedText);

      // 5. Remplacer les sauts de ligne
      formattedText = formattedText.replace(/\n/g, '<br>');

      // 6. Convertir la mise en forme markdown
      formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // 7. Surligner les termes clés seulement si demandé
      if (withHighlighting && this.extractedKeywords.length > 0) {
        formattedText = this.highlightKeywords(formattedText, this.extractedKeywords);
      }

      return DOMPurify.sanitize(formattedText, {
        ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 'br', 'li', 'ul', 'ol', 'p', 'div', 'span', 'a', 'mark', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
        ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'rel', 'data-ref-id', 'data-point-index', 'data-doc-index', 'data-source-key', 'title'],
      });
    },

    // Configuration de l'Intersection Observer pour optimiser le rendu des longs documents
    setupVirtualHighlighting() {
      // Ne s'applique que si nous avons beaucoup de contenu dans le panneau d'article
      const contentElement = document.querySelector('.article-details .scrollable-details-area'); // MODIFIED SELECTOR
      if (!contentElement) return;

      // Extraire les termes clés
      const keywords = this.extractedKeywords;
      if (!keywords.length) return;

      // Créer un observer qui ne traitera que les sections visibles
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Section visible, appliquer le surlignage dynamiquement
            this.applyHighlightingToSection(entry.target, keywords);
            // Une fois traité, ne plus observer cette section
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '100px 0px', // Préchargement avec une marge de 100px
        threshold: 0.1 // Déclencher quand 10% de l'élément est visible
      });

      // Observer les éléments enfants du contenu
      const contentSections = contentElement.children;
      Array.from(contentSections).forEach(section => {
        observer.observe(section);
      });
    },

    // Appliquer le surlignage à une section spécifique
    applyHighlightingToSection(section, keywords) {
      // Ne pas retraiter les sections déjà surlignées
      if (section.dataset.highlighted === 'true') return;

      // Parcourir tous les nœuds de texte de la section
      const textNodes = [];
      const walker = document.createTreeWalker(section, NodeFilter.SHOW_TEXT);

      let node;
      while ((node = walker.nextNode())) {
        // Ignorer les nœuds vides ou dans des éléments déjà surlignés
        if (node.nodeValue.trim() === '' ||
            node.parentElement.classList.contains('keyword-highlight') ||
            node.parentElement.classList.contains('inline-reference')) {
          continue;
        }
        textNodes.push(node);
      }

      // Élargir l'ensemble de termes à rechercher
      const expandedKeywords = this.expandKeywords(keywords);

      // Surligner chaque occurrence dans les nœuds de texte
      textNodes.forEach(textNode => {
        let nodeContent = textNode.nodeValue;
        expandedKeywords.forEach(keyword => {
          const regex = new RegExp(`\\b${this.escapeRegExp(keyword)}\\b`, 'gi');
          if (regex.test(nodeContent)) {
            // Remplacer le nœud texte par des éléments HTML avec surlignage
            const fragment = document.createDocumentFragment();
            let lastIndex = 0;

            // Reset regex pour commencer la recherche au début
            regex.lastIndex = 0;
            let match;
            while ((match = regex.exec(nodeContent)) !== null) {
              // Ajouter le texte avant la correspondance
              if (match.index > lastIndex) {
                fragment.appendChild(document.createTextNode(nodeContent.substring(lastIndex, match.index)));
              }

              // Ajouter la correspondance surlignée
              const span = document.createElement('span');
              span.className = 'keyword-highlight';
              span.textContent = match[0];
              fragment.appendChild(span);

              lastIndex = match.index + match[0].length;
            }

            // Ajouter le reste du texte
            if (lastIndex < nodeContent.length) {
              fragment.appendChild(document.createTextNode(nodeContent.substring(lastIndex)));
            }

            // Remplacer le nœud original par le fragment
            textNode.parentNode.replaceChild(fragment, textNode);
          }
        });
      });

      // Marquer la section comme traitée
      section.dataset.highlighted = 'true';
    },

    // Appliquer le surlignage uniquement au panneau des détails d'article
    applyKeywordHighlighting() {
      // Ne s'applique que si le panneau des détails est ouvert
      if (this.showArticlePanel) {
        this.$nextTick(() => {
          const detailsContainer = document.querySelector('.article-details .scrollable-details-area'); // MODIFIED SELECTOR
          if (detailsContainer && this.extractedKeywords.length > 0) {
            // Si nous avons beaucoup de contenu, utiliser la virtualisation
            if (detailsContainer.clientHeight > 2000) { // Check scrollHeight for content size
              this.setupVirtualHighlighting();
            } else {
              // Sinon appliquer directement aux enfants de detailsContainer
              Array.from(detailsContainer.children).forEach(childSection => {
                this.applyHighlightingToSection(childSection, this.extractedKeywords);
              });
            }
          }
        });
      }
    },

    // ========== Méthodes d'origine existantes ==========
    escapeHTML(text) {
      if (!text) return '';
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, m => map[m]);
    },

    getActesSocietesFullUrl(relativeUrl) {
      if (!relativeUrl) return null;
      const baseUrl = 'https://actespdf.blob.core.windows.net/actes-api/';
      return `${baseUrl}${relativeUrl}`;
    },

    processReferencesInText(text) {
      if (!text) return text;

      const expandedText = this.expandMultiReferences(text);
      // AJOUT de Mention dans la regex
      const refRegex = /((?:ID:\s+)?(?:Jurisprudence|Legiarti|Bofip|BOFiP|EuritextITA|EurLexITA|Euritext|CNN|BALO|Statuts|Convbi|Infofi|Infofin|Reglements|Règlement|Règlements|Reglement|BODACC|Energy|ActesSocietes|Concurrence|INPI|Mention|CNIL|BOSS|ACPR|AMF|CompCase|AGCM|GiustiziaAmm)\s*-\s*\d+)/g;

      return expandedText.replace(refRegex, (match) => {
        const refMatch = match.match(/^(?:ID:\s+)?(Jurisprudence|Legiarti|Bofip|BOFiP|EuritextITA|EurLexITA|Euritext|CNN|BALO|Statuts|Convbi|Infofi|Infofin|Reglements|Règlement|Règlements|Reglement|BODACC|Energy|ActesSocietes|Concurrence|INPI|Mention|CNIL|BOSS|ACPR|AMF|CompCase|AGCM|GiustiziaAmm)\s*-\s*(\d+)$/);

        if (!refMatch) return match;

        const refType = refMatch[1];
        const refIndex = parseInt(refMatch[2], 10) - 1;

        let sourceKey = null;
        if (refType === 'Jurisprudence') sourceKey = 'juritext';
        else if (refType === 'Legiarti') sourceKey = 'legiarti';
        else if (refType === 'Bofip') sourceKey = 'bofip';
        else if (refType === 'BOFiP') sourceKey = 'bofip';
        else if (refType === 'Euritext') sourceKey = 'euritext';
        else if (refType === 'CNN') sourceKey = 'cnn';
        else if (refType === 'BALO') sourceKey = 'balo';
        else if (refType === 'Statuts') sourceKey = 'statuts';
        else if (refType === 'Convbi') sourceKey = 'convbi';
        else if (refType === 'Infofi') sourceKey = 'infofi';
        else if (refType === 'Infofin') sourceKey = 'infofi';
        else if (refType === 'ActesSocietes') sourceKey = 'actes_societes';
        else if (refType === 'Reglements') sourceKey = 'reglements';
        else if (refType === 'Reglement') sourceKey = 'reglements';
        else if (refType === 'Règlements') sourceKey = 'reglements';
        else if (refType === 'Règlement') sourceKey = 'reglements';
        else if (refType === 'BODACC') sourceKey = 'bodacc';
        else if (refType === 'Energy') sourceKey = 'energy';
        else if (refType === 'Concurrence') sourceKey = 'concurrence';
        else if (refType === 'INPI') sourceKey = 'inpi';
        else if (refType === 'Mention') sourceKey = 'mention';
        else if (refType === 'CNIL') sourceKey = 'cnil';
        else if (refType === 'BOSS') sourceKey = 'boss';
              else if (refType === 'ACPR') sourceKey = 'acpr';
              else if (refType === 'AMF') sourceKey = 'amf';
              else if (refType === 'CompCase') sourceKey = 'eccomp';
              else if (refType === 'UPC') sourceKey = 'upc';
              else if (refType === 'EDPB') sourceKey = 'edpb';
              else if (refType === 'AGCM') sourceKey = 'agcm';
              else if (refType === 'GiustiziaAmm') sourceKey = 'giustizia_amministrativa';
              else if (refType === 'EuritextITA') sourceKey = 'jurisprudence_europe_italy';
              else if (refType === 'EurLexITA') sourceKey = 'eurlex_italy';

        if (sourceKey && this.indexLookup[sourceKey] && this.indexLookup[sourceKey][refIndex]) {
          const doc = this.indexLookup[sourceKey][refIndex];
          let docTitle;

          if (sourceKey === 'actes_societes') {
            if (doc.siren && doc.date_acte) {
              docTitle = this.$t('modals.legalAnalysis.references.companyActWithSiren', { siren: doc.siren, date: this.formatActesSocietesDate(doc.date_acte) });
            } else if (doc.title) {
              docTitle = doc.title;
            } else {
              docTitle = this.$t('modals.legalAnalysis.fallbacks.companyAct', { n: refIndex + 1 });
            }
          }
          else if (sourceKey === 'concurrence') {
            if (doc.numero_affaire) {
              docTitle = doc.numero_affaire;
            } else if (doc.titre) {
              docTitle = doc.titre;
            } else {
              docTitle = this.$t('modals.legalAnalysis.fallbacks.concurrenceDecision', { n: refIndex + 1 });
            }
          }
          else if (sourceKey === 'inpi') {
            if (doc.reference) {
              docTitle = `INPI - ${doc.date} - ${doc.reference}`;
            } else if (doc.parties) {
              docTitle = doc.parties;
            } else {
              docTitle = this.$t('modals.legalAnalysis.fallbacks.inpiDecision', { n: refIndex + 1 });
            }
          }
          else if (sourceKey === 'cnil') {
            docTitle = doc.title || doc.titre_complet || this.$t('modals.legalAnalysis.fallbacks.cnilDecision', { n: refIndex + 1 });
          }
          else if (sourceKey === 'boss') {
            docTitle = doc.title || this.$t('modals.legalAnalysis.fallbacks.bossSection', { n: refIndex + 1 });
          }
              else if (sourceKey === 'acpr') {
                docTitle = doc.title || doc.numero || this.$t('modals.legalAnalysis.fallbacks.acprDocument', { n: refIndex + 1 });
              }
              else if (sourceKey === 'amf') {
                docTitle = doc.title || doc.reference || this.$t('modals.legalAnalysis.fallbacks.amfDocument', { n: refIndex + 1 });
              }
              else if (sourceKey === 'eccomp') {
                docTitle = doc.title || doc.case_title || doc.case_number || this.$t('modals.legalAnalysis.fallbacks.ccDecision', { n: refIndex + 1 });
              }
              else if (sourceKey === 'edpb') {
                docTitle = doc.title || doc.slug || this.$t('modals.legalAnalysis.fallbacks.edpbDecision', { n: refIndex + 1 });
              }
              else if (sourceKey === 'upc') {
                if (doc.parties) {
                  docTitle = doc.parties;
                } else if (doc.court || doc.date_raw) {
                  const date = doc.date_raw || doc.date || '';
                  docTitle = `${doc.court || ''}${doc.court && date ? ' - ' : ''}${date}`.trim();
                } else {
                  docTitle = this.$t('modals.legalAnalysis.fallbacks.upcDecision', { n: refIndex + 1 });
                }
              }
          // Prioritize BOFIP identifiant if it exists
          else if (sourceKey === 'bofip' && doc.identifiant) {
            docTitle = doc.identifiant;
          }
          // Handle Statuts title from pdf_name
          else if (sourceKey === 'statuts' && doc.pdf_name) {
            docTitle = this.getStatutsTitle(doc.pdf_name);
          }
          // Fallback for all other cases
          else if (sourceKey === 'mention') {
            // Documents mentionnés par l'utilisateur
            docTitle = doc.title || this.$t('modals.legalAnalysis.fallbacks.citedDocument', { n: refIndex + 1 });
          }
          else {
            docTitle = doc.title || doc.textNumber || `${refType} #${refIndex + 1}`;
          }

          return `<a href="#" class="inline-reference" data-source="${sourceKey}" data-index="${refIndex}">${docTitle}</a>`;
        }

        return `<span class="inactive-reference">${match}</span>`;
      });
    },

    expandMultiReferences(text) {
        if (!text) return '';
        // AJOUT de Mention dans la regex
        const multiRefRegex = /((?:ID:\s+)?(?:Jurisprudence|Legiarti|Bofip|BOFiP|EuritextITA|EurLexITA|Euritext|CNN|BALO|Statuts|Convbi|Infofi|Reglements|Règlement|Règlements|Reglement|BODACC|Energy|ActesSocietes|Concurrence|INPI|Mention|CNIL|BOSS|ACPR|AMF|CompCase|AGCM|GiustiziaAmm))(\s*(?:-\s*|\s+))(\d+(?:\s*,\s*\d+)*)/g;

        return text.replace(multiRefRegex, (match, refType, sep, numbersPart) => {
            const numbers = numbersPart.split(/\s*,\s*/);
            return numbers.map(num => `${refType} - ${num}`).join(', ');
        });
    },

    /**
     * Construit le lien officiel à partir de l'ID (format unifié pour CE/CAA/TA).
     */
    buildOfficialLinkFromId(id) {
      if (!id) return null;
      if (id.startsWith('CETATEXT') || id.startsWith('JURITEXT')) {
        return `https://www.legifrance.gouv.fr/ceta/id/${id}`;
      }
      if (id.includes('_')) {
        const idNoXml = id.replace('.xml', '');
        return `https://opendata.justice-administrative.fr/recherche/shareFile/${idNoXml}`;
      }
      return `https://www.courdecassation.fr/decision/${id}`;
    },

    getOfficialDecisionLink() {
      const id = this.articleDetails.id;
      return this.buildOfficialLinkFromId(id) || '#';
    },


    handleInlineReferenceClick(event) {
      event.preventDefault();

      const refElement = event.target.closest('.inline-reference');

      if (refElement) {
        const sourceKey = refElement.getAttribute('data-source');
        const refIndex = parseInt(refElement.getAttribute('data-index'), 10);

        if (sourceKey && !isNaN(refIndex) && this.indexLookup[sourceKey] && this.indexLookup[sourceKey][refIndex]) {
          const doc = this.indexLookup[sourceKey][refIndex];
          const details = this.processDocumentBySource(doc, sourceKey, refIndex);
          this.showArticleDetails(details);
        }
      }
    },

    processDocumentBySource(doc, sourceKey, index) {
      let title = doc.title || doc.textNumber || this.$t('modals.legalAnalysis.fallbacks.genericWithType', { type: this.getSourceTypeName(sourceKey), n: index + 1 });

      // Cas spécial pour les statuts
      if (sourceKey === 'statuts' && doc.pdf_name) {
        title = this.getStatutsTitle(doc.pdf_name);
      }

      const details = {
        title: title,
        content: sourceKey === 'juritext' ? (doc.summary || '') : (doc.content || ''),
        source: sourceKey,
        id: doc.id,
        sourceData: doc // Pass the full doc object here
      };

      switch (sourceKey) {
        case 'juritext':
          details.texte = doc.texte || "";
          details.url = doc.link || doc.official_link || this.buildOfficialLinkFromId(doc.id);
          break;
        case 'reglements':
          details.celex = doc.celex;
          details.article_number = doc.article_number;
          details.chapter_title = doc.chapter_title;
          details.date = doc.date;
          details.content = doc.texte;
          break;
        case 'legiarti':
          details.hierarchy = doc.hierarchy;
          details.annotation = doc.annotation || "";
          break;
        case 'bofip':
          details.url = doc.url;
          break;
        case 'euritext':
          if (doc.links) {
            details.links = doc.links;
          }
          break;
        case 'cnn':
          details.etat = doc.etat;
          details.date_debut = doc.date_debut;
          details.date_fin = doc.date_fin;
          details.cid_tex = doc.cid_tex;
          details.article_id = doc.article_id;
          break;
        case 'balo':
          details.id_annonce = doc.id_annonce;
          details.dateparution = doc.dateparution;
          details.societes_noms = doc.societes_noms;
          details.siren = doc.siren;
          details.numero_affaire = doc.numero_affaire;
          details.categorie_libelle = doc.categorie_libelle;
          details.parution_numero = doc.parution_numero;
          break;
        case 'statuts':
          details.pdf_name = doc.pdf_name;
          details.slugged_title = doc.slugged_title;
          break;
        case 'convbi': // <-- ADD THIS CASE
          details.pays = doc.pays;
          details.titre_lien = doc.titre_lien;
          details.pdf_url = doc.pdf_url;
          details.article_num = doc.article_num;
          details.texte = doc.texte;
          break;
        case 'infofi': // <-- AJOUTER CE CAS DANS LE SWITCH
          details.nom_societe = doc.nom_societe;
          details['Titre opération'] = doc['Titre opération']; // <-- AJOUTER
          details.Lien_Document = doc.Lien_Document; // <-- AJOUTER
          details.date_document = doc.date_document;
          details.description_information = doc.description_information;
          details.id_information = doc.id_information;
          break;
        case 'actes_societes':
          details.siren = doc.siren;
          details.acte_id = doc.acte_id;
          details.date_acte = doc.date_acte;
          details.summary = doc.summary;
          details.keywords = doc.keywords;
          details.chunk_number = doc.chunk_number;
          details.total_pages = doc.total_pages;
          if (doc.source_path) {
            details.source_path = doc.source_path;
          } else if (doc.source && doc.source !== 'actes_societes') {
            details.source_path = doc.source;
          }
          break;
        case 'bodacc':
          details.doc_id = doc.doc_id;
          details.dateparution = doc.dateparution;
          details.familleavis = doc.familleavis;
          details.typeavis = doc.typeavis;
          details.url_complete = doc.url_complete;
          break;
        case 'energy':
          details.doc_id = doc.doc_id;
          details.url = doc.url;
          break;
        case 'concurrence':
          details.id_document = doc.id_document;
          details.type_document = doc.type_document;
          details.numero_affaire = doc.numero_affaire;
          details.titre = doc.titre;
          details.date_decision = doc.date_decision;
          details.secteur = doc.secteur;
          details.parties = doc.parties;
          details.sens_decision = doc.sens_decision;
          details.url_page = doc.url_page;
          break;
        case 'cnil':
          details.id = doc.id;
          details.nature = doc.nature;
          details.date = doc.date;
          details.titre_complet = doc.titre_complet;
          details.content = doc.summary || '';
          details.url = doc.link || doc.url;
          break;
        case 'boss':
          details.date = doc.date;
          details.content = doc.summary || '';
          details.url = doc.link || doc.url;
          break;
            case 'acpr':
              details.acpr_source = doc.acpr_source;
              details.doc_subtype = doc.doc_subtype;
              details.categorie = doc.categorie;
              details.themes = doc.themes;
              details.numero = doc.numero;
              details.date = doc.date;
              details.url = doc.link || doc.url;
              details.content = doc.summary || '';
              break;
            case 'amf':
              details.amf_source = doc.amf_source;
              details.doc_type = doc.doc_type;
              details.reference = doc.reference;
              details.themes = doc.themes;
              details.montant = doc.montant;
              details.sanctioned_entity = doc.sanctioned_entity;
              details.date = doc.date;
              details.url = doc.link || doc.url;
              details.content = doc.summary || '';
              break;
            case 'eccomp':
              details.case_number = doc.case_number;
              details.case_title = doc.case_title;
              details.instrument = doc.instrument;
              details.decision_type = doc.decision_type;
              details.decision_reference = doc.decision_reference;
              details.decision_language = doc.decision_language;
              details.member_state = doc.member_state;
              details.companies = doc.companies;
              details.legal_basis = doc.legal_basis;
              details.sectors = doc.sectors;
              details.date = doc.date;
              details.year = doc.year;
              details.url = doc.link || doc.url;
              details.content = doc.summary || doc.content || '';
              details.texte = doc.full_text || doc.texte || '';
              break;
            case 'edpb':
              details.title = doc.title;
              details.slug = doc.slug;
              details.publication_types = doc.publication_types;
              details.topics = doc.topics;
              details.members = doc.members;
              details.status = doc.status;
              details.date = doc.date_raw || doc.date;
              details.summary = doc.summary;
              details.key_points = doc.key_points;
              details.keywords = doc.keywords;
              details.url = doc.url;
              details.pdf_url_used = doc.pdf_url_used;
              details.word_count = doc.word_count;
              details.content = doc.summary || doc.key_points || '';
              details.texte = doc.texte || '';
              break;
            case 'upc':
              details.case_numbers = doc.case_numbers;
              details.registry_number = doc.registry_number;
              details.court = doc.court;
              details.action_type = doc.action_type;
              details.parties = doc.parties;
              details.date = doc.date_raw || doc.date;
              details.language_of_proceedings = doc.language_of_proceedings;
              details.document_language = doc.document_language;
              details.summary = doc.summary;
              details.headnotes = doc.headnotes;
              details.keywords = doc.keywords;
              details.detail_url = doc.detail_url;
              details.pdf_url = doc.pdf_url;
              details.url = doc.detail_url || doc.pdf_url;
              details.content = doc.summary || doc.headnotes || '';
              details.texte = doc.texte || '';
              break;
        case 'inpi':
          details.id_doc = doc.id_doc;
          details.domaine = doc.domaine;
          details.reference = doc.reference;
          details.juridiction = doc.juridiction;
          details.parties = doc.parties;
          details.date = doc.date;
          details.url_document_final = doc.url_document_final;
          details.content = doc.texte || doc.text_complet || doc.content || '';
          break;
        case 'mention': // NOUVEAU CAS pour les documents mentionnés
          details.type = doc.type;
          details.date = doc.date;
          details.jurisdiction = doc.jurisdiction;
          details.chamber = doc.chamber;
          details.case_number = doc.case_number;
          details.article_number = doc.article_number;
          details.code_name = doc.code_name;
          details.celex = doc.celex;
          details.takeaway = doc.takeaway;
          details.original_reference = doc.original_reference;
          details.is_user_cited = doc.is_user_cited;
          // Priority: doc.content (from backend), fallback to takeaway or full_text
          details.content = doc.content || doc.takeaway || doc.full_text || '';
          // Map official_link or link to url for consistent display
          details.url = doc.link || doc.official_link;
          break;
        case 'agcm':
          details.provvedimento_number = doc.provvedimento_number || doc.numero_provvedimento;
          details.tipo_provvedimento = doc.tipo_provvedimento || doc.provvedimento_type;
          details.materia = doc.materia || doc.subject;
          details.parti = doc.parti || doc.parties;
          details.date = doc.date || doc.data;
          details.url = doc.link || doc.lien || doc.url;
          details.content = doc.summary || doc.content || '';
          details.texte = doc.full_text || doc.texte || '';
          break;
        case 'giustizia_amministrativa':
          details.tribunale = doc.tribunale || doc.court;
          details.sezione = doc.sezione || doc.section;
          details.numero = doc.numero || doc.number;
          details.tipo_decisione = doc.tipo_decisione || doc.decision_type;
          details.oggetto = doc.oggetto || doc.subject;
          details.parti = doc.parti || doc.parties;
          details.date = doc.date || doc.data;
          details.anno = doc.anno;
          details.tipo = doc.tipo;
          details.url = doc.link || doc.lien || doc.url;
          details.content = doc.summary || doc.content || '';
          details.texte = doc.full_text || doc.texte || '';
          break;
        case 'jurisprudence_europe_italy': {
          const linkUrls = doc.link_urls || {};
          details.celex = linkUrls.CELEX || doc.celex;
          details.ecli = linkUrls.ECLI || doc.ecli;
          details.cellar = linkUrls.Cellar;
          details.doc_type = doc.doc_type || doc.decision_type || doc.type;
          details.court = doc.court;
          details.subject_matter = doc.subject_matter || doc.materia;
          details.date = doc.date || doc.data;
          details.url = doc.link || doc.lien || doc.url;
          details.content = doc.summary || doc.content || '';
          details.texte = doc.full_text || doc.texte || '';
          break;
        }
      }

      return details;
    },

    // Helper method to extract sort order from id_answer
    getSortOrder(doc) {
      if (doc.id_answer) {
        const match = doc.id_answer.match(/(\w+)\s*-\s*(\d+)$/);
        if (match) {
          return parseInt(match[2], 10);
        }
      }
      return 999; // Put documents without proper id_answer at the end
    },

    // Add this method to extract the display number from id_answer
    getDisplayNumber(doc) {
      if (doc.id_answer) {
        const match = doc.id_answer.match(/(\w+)\s*-\s*(\d+)$/);
        if (match) {
          return parseInt(match[2], 10);
        }
      }
      return null;
    },

    buildSourceIndexes() {
      if (!this.pointData || !this.pointData.analyses) return;

      const indexLookup = {
        juritext: {}, legiarti: {}, bofip: {}, euritext: {},
        cnn: {}, balo: {}, bodacc: {}, statuts: {}, convbi: {},
        infofi: {}, actes_societes: {}, reglements: {}, energy: {}, concurrence: {}, inpi: {}, cnil: {}, boss: {}, acpr: {}, amf: {}, eccomp: {}, upc: {}, edpb: {}, mention: {},
        agcm: {}, giustizia_amministrativa: {}, jurisprudence_europe_italy: {}
      };

      this.pointData.analyses.forEach(sourceBlock => {
        const sourceKey = sourceBlock.source;
        if (!indexLookup[sourceKey]) return;

        sourceBlock.analysis.forEach((doc) => {
          // Extract the index from id_answer field
          if (doc.id_answer) {
            // Parse "Jurisprudence - 1" to get index 0 (1-based to 0-based)
            const match = doc.id_answer.match(/(\w+)\s*-\s*(\d+)$/);
            if (match) {
              const docIndex = parseInt(match[2], 10) - 1; // Convert to 0-based index
              indexLookup[sourceKey][docIndex] = doc;
            }
          }
        });
      });

      // NOUVEAU : Indexer les documents mentionnés par l'utilisateur
      if (this.pointData.mentioned_docs && Array.isArray(this.pointData.mentioned_docs)) {
        this.pointData.mentioned_docs.forEach((doc) => {
          if (doc.id_answer) {
            // Parse "Mention - 1" to get index
            const match = doc.id_answer.match(/Mention\s*-\s*(\d+)$/);
            if (match) {
              const docIndex = parseInt(match[1], 10) - 1;
              indexLookup.mention[docIndex] = doc;
            }
          }
        });
      }

      this.indexLookup = indexLookup;
    },

    getSourceTitle(sourceType) {
      const titles = {
        'legiarti': this.$t('modals.legalAnalysis.sourceTitles.legiarti'),
        'juritext': this.$t('modals.legalAnalysis.sourceTitles.juritext'),
        'bofip': this.$t('modals.legalAnalysis.sourceTitles.bofip'),
        'euritext': this.$t('modals.legalAnalysis.sourceTitles.euritext'),
        'cnn': this.$t('modals.legalAnalysis.sourceTitles.cnn'),
        'balo': this.$t('modals.legalAnalysis.sourceTitles.balo'),
        'bodacc': this.$t('modals.legalAnalysis.sourceTitles.bodacc'),
        'statuts': this.$t('modals.legalAnalysis.sourceTitles.statuts'),
        'convbi': this.$t('modals.legalAnalysis.sourceTitles.convbi'),
        'infofi': this.$t('modals.legalAnalysis.sourceTitles.infofi'),
        'reglements': this.$t('modals.legalAnalysis.sourceTitles.reglements'),
        'energy': this.$t('modals.legalAnalysis.sourceTitles.energy'),
        'concurrence': this.$t('modals.legalAnalysis.sourceTitles.concurrence'),
        'inpi': this.$t('modals.legalAnalysis.sourceTitles.inpi'),
        'cnil': this.$t('modals.legalAnalysis.sourceTitles.cnil'),
        'boss': this.$t('modals.legalAnalysis.sourceTitles.boss'),
        'acpr': this.$t('modals.legalAnalysis.sourceTitles.acpr'),
        'amf': this.$t('modals.legalAnalysis.sourceTitles.amf'),
        'eccomp': this.$t('modals.legalAnalysis.sourceTitles.eccomp'),
        'upc': this.$t('modals.legalAnalysis.sourceTitles.upc'),
        'edpb': this.$t('modals.legalAnalysis.sourceTitles.edpb'),
        'mention': this.$t('modals.legalAnalysis.sourceTitles.mention'),
      };
      return titles[sourceType] || sourceType;
    },

    formatBodaccFamily(family) {
      if (!family) return '';
      // Capitaliser la première lettre
      return family.charAt(0).toUpperCase() + family.slice(1).toLowerCase();
    },

    formatBodaccType(type) {
      if (!type) return '';
      return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    },

    formatBodaccDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch (e) {
        return dateString;
      }
      return dateString;
    },

    getSourceTypeName(sourceKey) {
      const names = {
        'legiarti': this.$t('modals.legalAnalysis.sourceTypeNames.legiarti'),
        'juritext': this.$t('modals.legalAnalysis.sourceTypeNames.juritext'),
        'bofip': this.$t('modals.legalAnalysis.sourceTypeNames.bofip'),
        'euritext': this.$t('modals.legalAnalysis.sourceTypeNames.euritext'),
        'cnn': this.$t('modals.legalAnalysis.sourceTypeNames.cnn'),
        'balo': this.$t('modals.legalAnalysis.sourceTypeNames.balo'),
        'statuts': this.$t('modals.legalAnalysis.sourceTypeNames.statuts'),
        'convbi': this.$t('modals.legalAnalysis.sourceTypeNames.convbi'),
        'infofi': this.$t('modals.legalAnalysis.sourceTypeNames.infofi'),
        'actes_societes': this.$t('modals.legalAnalysis.sourceTypeNames.actesSocietes'),
        'reglements': this.$t('modals.legalAnalysis.sourceTypeNames.reglements'),
        'bodacc': this.$t('modals.legalAnalysis.sourceTypeNames.bodacc'),
        'energy': this.$t('modals.legalAnalysis.sourceTypeNames.energy'),
        'concurrence': this.$t('modals.legalAnalysis.sourceTypeNames.concurrence'),
        'inpi': this.$t('modals.legalAnalysis.sourceTypeNames.inpi'),
        'cnil': this.$t('modals.legalAnalysis.sourceTypeNames.cnil'),
        'boss': this.$t('modals.legalAnalysis.sourceTypeNames.boss'),
        'acpr': this.$t('modals.legalAnalysis.sourceTypeNames.acpr'),
        'amf': this.$t('modals.legalAnalysis.sourceTypeNames.amf'),
        'eccomp': this.$t('modals.legalAnalysis.sourceTypeNames.eccomp'),
        'upc': this.$t('modals.legalAnalysis.sourceTypeNames.upc'),
        'edpb': this.$t('modals.legalAnalysis.sourceTypeNames.edpb'),
        'mention': this.$t('modals.legalAnalysis.sourceTypeNames.mention')
      };
      return names[sourceKey] || this.$t('modals.legalAnalysis.sourceTypeNames.default');
    },

    formatEtat(etat) {
      const etatMap = {
        'VIGUEUR_NON_ETEN': this.$t('modals.legalAnalysis.etat.vigueurNonEten'),
        'VIGUEUR_ETEN': this.$t('modals.legalAnalysis.etat.vigueurEten'),
        'PERIME': this.$t('modals.legalAnalysis.etat.perime'),
        'ABROGE': this.$t('modals.legalAnalysis.etat.abroge'),
        'REMPLACE': this.$t('modals.legalAnalysis.etat.remplace'),
        'MODIFIE': this.$t('modals.legalAnalysis.etat.modifie')
      };
      return etatMap[etat] || etat;
    },

    getEtatClass(etat) {
      const etatClasses = {
        'VIGUEUR_NON_ETEN': 'etat-vigueur',
        'VIGUEUR_ETEN': 'etat-vigueur-etendu',
        'PERIME': 'etat-perime',
        'ABROGE': 'etat-abroge',
        'REMPLACE': 'etat-remplace',
        'MODIFIE': 'etat-modifie'
      };
      return etatClasses[etat] || 'etat-default';
    },


    getSourceItems(sourceType) {
      // ⭐ AJOUT : Cas spécial pour les documents mentionnés (stockés à la racine)
      if (sourceType === 'mention') {
        return this.pointData?.mentioned_docs || [];
      }

      if (!this.pointData || !this.pointData.analyses) return [];

      const sourceBlock = this.pointData.analyses.find(block => block.source === sourceType);
      return sourceBlock && sourceBlock.analysis ? sourceBlock.analysis : [];
    },

    toggleBodyScroll(enable) {
      if (enable) {
        document.body.classList.remove('modal-open');
      } else {
        document.body.classList.add('modal-open');
      }
    }
  }
};
</script>

<style>

.article-html {
  font-size: 16px;   /* ajustez à votre goût */
  line-height: 1.7;
}

.infofi-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.infofi-company {
  font-weight: 600;
  color: #0d9488; /* Teal color for finance */
  margin-right: 10px;
}

.infofi-date {
  font-size: 11px;
}

.infofi-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #0d9488; /* Teal border */
  border-radius: 0 6px 6px 0;
}

.infofi-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #0d9488, transparent);
  margin-top: 15px;
}

.infofi-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.infofi-info-item strong {
  color: #0d9488;
  margin-right: 8px;
  min-width: 150px; /* Adjusted width */
}

.infofi-company-name, .infofi-doc-date, .infofi-type {
  font-weight: 600;
}

.convbi-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.convbi-country {
  background-color: #f0f9ff;
  color: #0369a1;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.convbi-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #059669; /* Green for fiscal */
  border-radius: 0 6px 6px 0;
}

.convbi-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #059669, transparent);
  margin-top: 15px;
}

.convbi-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.convbi-info-item strong {
  color: #059669;
  margin-right: 8px;
}

.convbi-country-name {
  font-weight: 600;
}

.statuts-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.statuts-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.statuts-info-item strong {
  color: #625CFB;
  margin-right: 8px;
  min-width: 120px;
}

.statuts-link {
  text-decoration: none;
  font-weight: 600;
}

.statuts-link:hover {
  text-decoration: underline;
}

/* Separator between metadata and content for Statuts */
.statuts-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

/* BALO specific styles */
.balo-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.balo-company {
  font-weight: 600;
  color: #625CFB;
  margin-right: 10px;
}

.balo-category {
  background-color: #f0f9ff;
  color: #0369a1;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  margin-right: 8px;
}

.balo-date {
  font-size: 11px;
}

.balo-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.balo-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.balo-info-item strong {
  color: #625CFB;
  margin-right: 8px;
  min-width: 120px;
}

.balo-company-name {
  font-weight: 600;
  color: #625CFB;
}

.balo-siren {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.balo-date { /* This might conflict with the one above if not careful, but context should differentiate */
  font-family: 'Monaco', 'Menlo', monospace;
}

.balo-bulletin {
  font-weight: 600;
  color: #7c3aed;
}

.balo-affaire {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.balo-category-badge {
  color: #1e40af;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.balo-link {
  text-decoration: none;
  font-weight: 600;
}

.balo-link:hover {
  text-decoration: underline;
}

/* Separator between metadata and content for BALO */
.balo-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

.cnn-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.cnn-status, .cnn-validity, .cnn-reference {
  margin-bottom: 8px;
  font-size: 14px;
}

.cnn-status strong, .cnn-validity strong, .cnn-reference strong {
  color: #625CFB;
  margin-right: 8px;
}

/* Status indicators for different états */
.etat-vigueur {
  color: #625CFB;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #d1fae5;
  border-radius: 12px;
  font-size: 12px;
}

.etat-vigueur-etendu {
  color: #059669;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #a7f3d0;
  border-radius: 12px;
  font-size: 12px;
}

.etat-perime {
  color: #f59e0b;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #fef3c7;
  border-radius: 12px;
  font-size: 12px;
}

.etat-abroge {
  color: #ef4444;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #fecaca;
  border-radius: 12px;
  font-size: 12px;
}

.etat-remplace {
  color: #8b5cf6;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #e9d5ff;
  border-radius: 12px;
  font-size: 12px;
}

.etat-modifie {
  color: #3b82f6;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #dbeafe;
  border-radius: 12px;
  font-size: 12px;
}

.etat-default {
  color: #6b7280;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #f3f4f6;
  border-radius: 12px;
  font-size: 12px;
}

/* Separator between metadata and content */
.cnn-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

.decisions-links {
  display: flex;
  flex-direction: column;
}

.annotation-section {
  margin-top: 30px;
  padding: 15px;
  background-color: #f5f5ff;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.annotation-title {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 16px;
  color: #625CFB;
  font-weight: bold;
}

.annotation-content {
  line-height: 1.6;
  font-size: 14px;
}

/* Add a subtle separator between the article and annotation */
.annotation-section::before {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-bottom: 15px;
}

/* Styles globaux (non-scoped) */
.inline-reference {
  color: #625CFB !important;
  font-style: italic !important;
  text-decoration: underline !important;
  cursor: pointer !important;
  transition: color 0.2s ease, background-color 0.2s ease !important;
  padding: 0 2px !important;
  border-radius: 2px !important;
  font-size: 0.9em !important;
}

.inline-reference:hover {
  color: #4338ca !important;
  background-color: rgba(99, 102, 241, 0.1) !important;
}

.inactive-reference {
  color: #6b7280 !important;
  font-style: italic !important;
  text-decoration: underline dotted !important;
}

/* Style pour le surlignage des termes clés */
.keyword-highlight {
  background-color: rgba(98, 92, 251, 0.2); /* Couleur violette transparente */
  color: #625CFB; /* Texte en violet */
  border-radius: 2px;
  padding: 0 2px;
  font-weight: 600;
  display: inline;
  position: relative;
}

/* Animation subtile au survol */
.keyword-highlight:hover {
  background-color: rgba(98, 92, 251, 0.3);
  transition: background-color 0.2s ease;
}

/* Bordure inférieure pour accentuer le surlignage */
.keyword-highlight::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #625CFB;
  opacity: 0.6;
}

/* Style pour fixed body */
.modal-open {
  overflow: hidden;
  position: fixed;
  width: 100%;
  height: 100%;
}

/* NOUVEAU STYLE POUR REF-SECTION */
.ref-section {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB; /* Bordure verte pour les références */
  border-radius: 0 6px 6px 0;
  font-size: 14px;
}

.ref-title {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 16px;
  color: #625CFB; /* Vert pour le titre */
  font-weight: bold;
}

.ref-content {
  margin-bottom: 10px;
  color: #625CFB;
}

.ref-content .decisions-links,
.ref-content .source-url,
.ref-content .cnn-reference,
.ref-content .balo-info-item,
.ref-content .euritext-links,
.ref-content .statuts-info-item {
  margin-bottom: 8px;
}

.ref-content a {
  color: #625CFB; /* Vert pour les liens dans les références */
  text-decoration: none;
  font-weight: 600;
}

.ref-content a:hover {
  text-decoration: underline;
}

/* Séparateur entre ref-section et citations-section */
.ref-section::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

/* STYLES POUR CITATIONS SECTION */
.citations-section {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB; /* Blue border */
  border-radius: 0 6px 6px 0;
  font-size: 14px;
}

.citations-title {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 16px;
  color: #625CFB; /* Darker blue for title */
  font-weight: bold;
}

.citation-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.citation-item {
  background-color: #ffffff; /* White background for each citation */
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.citation-item strong {
  color: #333; /* Darker text for theme */
  display: block;
  margin-bottom: 8px;
}

.citation-text {
  font-style: italic;
  color: #555;
  margin: 0;
  line-height: 1.5;
  border-left: 3px solid #ccc; /* Subtle left border for the quote */
  padding-left: 10px;
}

/* Separator between citations section and details-content */
.citations-section::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 20px;
}
</style>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Roboto', sans-serif;
}

.modal-content {
  width: 90%;
  height: 90vh;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  background-color: #f2f2f2;
  padding: 10px;
  border-bottom: 1px solid #eaeaea;
  flex-shrink: 0;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-left {
  width: 20%;
}

.tabs {
  display: flex;
  gap: 10px;
  justify-content: center;
  background-color: #e3e3e4;
  border-radius: 6px;
  width: 60%;
}

.header-right {
  width: 20%;
  text-align: right;
}

.tab-button {
  flex: 1;
  background: none;
  border: none;
  padding: 10px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  border-radius: 6px;
  color: #333;
  background-color: #e3e3e4;
  transition: background-color 0.3s;
}

.tab-button.active {
  background-color: #FFF;
  font-weight: bold;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 2px;
}

.tab-button:hover:not(.active) {
  background-color: #d1d1d2;
}

.close-button, .close-details {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
}

.tab-content {
  display: flex;
  flex-direction: column;
  padding: 3em;
  height: calc(100% - 60px); /* Adjusted for modal-header height */
  overflow: hidden;
}

.content-title {
  margin-top: 0;
  margin-bottom: 1.5em;
  font-size: 1.1em;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0; /* Prevent title from shrinking */
  text-align: center;
}

.main-content-container {
  display: flex;
  flex-grow: 1; /* Allow container to take available space */
  gap: 20px;
  overflow: hidden; /* Important for child overflow to work */
  justify-content: center;
  width: 73%;
  margin: 0 auto;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.6;
  justify-content: center;
  display: flex;
}

.article-content {
  line-height: 1.6;
  text-align: justify;
  width: 90%;
}

.article-link {
  color: #625CFB;
  cursor: pointer;
  text-decoration: underline;
  margin-right: 10px;
}

.source-block {
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eaeaea;
}

.source-items {
  margin-top: 15px;
}

.source-item {
  margin-bottom: 10px;
}

.juritext-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  gap: 15px;
}

.summary-section-title {
  margin-bottom: 2em;
  font-size: 16px;
  color: #625CFB;
  font-weight: bold;
}

.export-word-button-inline {
  background: #625CFB;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.export-word-button-inline:hover {
  background-color: #4f46e5;
}

.export-word-button-inline svg {
  width: 14px;
  height: 14px;
}

.article-details {
  width: 60%;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* This is key for the inner scrollable div */
  border-radius: 6px;
  border: 1px solid #DFDFDF;
}

.bg-blue-color {
  background-color: #f2f2ff;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 15px;
  border-bottom: 1px solid #eaeaea;
  flex-shrink: 0; /* Prevent header from shrinking */
}

.details-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
}

/* NEW STYLE FOR SCROLLABLE AREA */
.scrollable-details-area {
  flex-grow: 1; /* Takes up remaining vertical space */
  overflow-y: auto; /* Enables vertical scrolling */
  /* Padding for the overall scrollable area can be added here if needed,
     e.g., padding: 0 20px 20px 20px;
     For now, individual .details-content and .citations-section have their own padding.
  */
}

.details-content {
  padding: 20px;
  /* overflow-y: auto; REMOVED - now handled by .scrollable-details-area */
  font-size: 14px;
  text-align: justify;
}

.hierarchy {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eaeaea;
  color: #625CFB;
  font-size: 0.95em;
}

.decision-meta {
  margin-bottom: 15px;
  padding: 10px;
  border-radius: 5px;
}

.full-decision {
  margin-top: 20px;
}

.full-decision a {
  color: #625CFB;
  cursor: pointer;
  text-decoration: underline;
  display: inline-block;
  margin-bottom: 10px;
  font-weight: bold;
}

.source-url {
  margin-bottom: 20px;
  border-radius: 5px;
}

.source-url a {
  color: #625CFB;
  text-decoration: none;
}

.euritext-links {
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 5px;
}

.euritext-link {
  margin-top: 8px;
}

.euritext-link a {
  color: #625CFB;
  text-decoration: underline;
}

.empty-message {
  color: #888;
  font-style: italic;
  text-align: center;
  margin-top: 20px;
}

.highlight {
  color: #625CFB;
  text-decoration: underline;
}

/* Responsive styles */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    height: 95vh;
  }
  .tab-content {
    padding: 1.5em; /* Reduced padding on smaller screens */
  }

  .main-content-container {
    flex-direction: column;
  }

  .main-content {
    max-height: 50%; /* Adjust as needed */
  }

  .article-details {
    width: 100%;
    flex-grow: 1; /* Allow article details to take remaining space in column */
    max-height: none; /* Remove fixed max-height, let flexbox handle it */
  }

  .tabs {
    width: 100%;
  }

  .tab-button {
    padding: 8px;
    font-size: 11px;
  }

  .header-left, .header-right {
    display: none;
  }
}

/* Styles pour les règlements européens */
.reglements-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.reglements-chapter {
  background-color: #e0f2fe;
  color: #0369a1;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.reglements-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.reglements-info-item strong {
  color: #0369a1;
  margin-right: 8px;
  min-width: 120px;
}

.reglements-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #0369a1;
  border-radius: 0 6px 6px 0;
}

.reglements-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #0369a1, transparent);
  margin-top: 15px;
}

.inline-reference.temp-highlight {
  background-color: rgb(252, 219, 8) !important; /* A light yellow color */
  box-shadow: 0 0 0 2px #ffeeba !important;
  transition: background-color 0.5s ease, box-shadow 0.5s ease;
  border-radius: 4px;
}

/* Styles pour BODACC */
.bodacc-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.bodacc-family {
  font-weight: 600;
  color: #7c2d12; /* Couleur marron pour BODACC */
  margin-right: 10px;
}

.bodacc-type {
  background-color: #fef7ed;
  color: #c2410c;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  margin-right: 8px;
}

.bodacc-date {
  font-size: 11px;
}

.bodacc-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #7c2d12;
  border-radius: 0 6px 6px 0;
}

.bodacc-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #7c2d12, transparent);
  margin-top: 15px;
}

.bodacc-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.bodacc-info-item strong {
  color: #7c2d12;
  margin-right: 8px;
  min-width: 120px;
}

.bodacc-family-badge, .bodacc-type-badge {
  background-color: #fef7ed;
  color: #c2410c;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* Energy specific styles */
.energy-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.energy-type {
  background-color: #fef3c7;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.energy-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-radius: 0 6px 6px 0;
}

.energy-metadata::after {
  content: "";
  display: block;
  height: 1px;
  margin-top: 15px;
}

.energy-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.energy-info-item strong {
  margin-right: 8px;
  min-width: 120px;
}

/* Styles pour Actes de Sociétés */
.actes-societes-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.actes-societes-siren {
  font-weight: 600;
  color: #7c3aed; /* Violet pour les actes de sociétés */
  margin-right: 10px;
  font-family: 'Monaco', 'Menlo', monospace;
}

.actes-societes-date {
  font-size: 11px;
  margin-right: 8px;
}

.actes-societes-keywords {
  background-color: #f3e8ff;
  color: #7c3aed;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  font-style: italic;
}

.actes-societes-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #7c3aed; /* Violet pour les actes de sociétés */
  border-radius: 0 6px 6px 0;
}

.actes-societes-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #7c3aed, transparent);
  margin-top: 15px;
}

.actes-societes-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.actes-societes-info-item strong {
  margin-right: 8px;
  min-width: 150px;
}

.actes-societes-siren-value {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.actes-societes-date-full, .actes-societes-summary, .actes-societes-keywords-full {
  font-weight: 600;
}

.actes-societes-summary {
  line-height: 1.5;
  color: #4b5563;
}

.actes-societes-pages {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.actes-societes-summary-section {
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #7c3aed;
  border-radius: 0 6px 6px 0;
}

.summary-title {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 15px;
  color: #7c3aed;
  font-weight: bold;
}

.summary-content {
  line-height: 1.6;
  font-size: 14px;
  text-align: justify;
}

.actes-societes-content-section {
  margin-top: 20px;
}

/* Separator between summary section and content */
.actes-societes-summary-section::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #7c3aed, transparent);
  margin-top: 15px;
}

/* Style adjustments for reference section items */
.ref-content .actes-societes-info-item {
  margin-bottom: 10px;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.ref-content .actes-societes-info-item strong {
  margin-right: 8px;
  min-width: 120px;
}

.ref-content .actes-societes-siren-value {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.ref-content .actes-societes-date-full {
  font-weight: 600;
  color: #4b5563;
}

/* Styles pour Autorité de la concurrence */
.concurrence-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.concurrence-type {
  background-color: #fef3c7;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 8px;
}

.concurrence-sector {
  font-weight: 600;
  margin-right: 10px;
}

.concurrence-date {
  font-size: 11px;
}

.concurrence-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-radius: 0 6px 6px 0;
}

.concurrence-metadata::after {
  content: "";
  display: block;
  height: 1px;
  margin-top: 15px;
}

.concurrence-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.concurrence-info-item strong {
  margin-right: 8px;
  min-width: 150px;
}

.concurrence-type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.concurrence-affaire-num {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.concurrence-date-full, .concurrence-sector-name, .concurrence-parties {
  font-weight: 600;
}

.concurrence-affaire {
  font-family: 'Monaco', 'Menlo', monospace;
  font-weight: 600;
}

/* Decision status styles */
.decision-sanction {
  color: #dc2626;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #fecaca;
  border-radius: 12px;
  font-size: 12px;
}

.decision-accepted {
  color: #059669;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #d1fae5;
  border-radius: 12px;
  font-size: 12px;
}

.decision-rejected {
  color: #f59e0b;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #fef3c7;
  border-radius: 12px;
  font-size: 12px;
}

.decision-default {
  color: #6b7280;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #f3f4f6;
  border-radius: 12px;
  font-size: 12px;
}

/* Styles pour INPI (Jurisprudence propriété intellectuelle) */
.inpi-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.inpi-domain {
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 8px;
}

.inpi-jurisdiction {
  font-weight: 600;
  margin-right: 10px;
}

.inpi-date {
  font-size: 11px;
}

.inpi-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-radius: 0 6px 6px 0;
}

.inpi-metadata::after {
  content: "";
  display: block;
  height: 1px;
  margin-top: 15px;
}

.inpi-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.inpi-info-item strong {
  margin-right: 8px;
  min-width: 150px;
}

.inpi-domain-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.inpi-reference-num {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.inpi-date-full, .inpi-jurisdiction-name, .inpi-parties {
  font-weight: 600;
}

.inpi-reference {
  font-family: 'Monaco', 'Menlo', monospace;
  font-weight: 600;
}

/* INPI Domain styles */
.domain-marques {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.domain-brevets {
  background-color: #d1fae5;
  color: #059669;
}

.domain-dessins {
  background-color: #ede9fe;
  color: #7c3aed;
}

.domain-default {
  background-color: #e0f2fe;
  color: #0369a1;
}

/* Styles pour Mention (documents cités) */
.mention-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.mention-type {
  background-color: #f3e8ff;
  color: #6b21a8;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 8px;
}

.mention-jurisdiction {
  font-weight: 600;
  margin-right: 10px;
}

.mention-date {
  font-size: 11px;
}


/* Styles pour les liens officiels génériques */
.official-link-section {
  margin: 16px 0;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.official-link-item {
  display: flex;
  align-items: center;
}

.official-link-button {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background-color: #625CFB;
  color: white !important;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  text-decoration: none !important;
  transition: background-color 0.2s;
}

.official-link-button:hover {
  background-color: #4f46e5;
  text-decoration: none !important;
}

/* Styles pour la CNIL */
.cnil-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.cnil-nature-badge {
  background-color: #ede9fe;
  color: #625CFB;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 8px;
}

.cnil-date {
  font-size: 11px;
}

.cnil-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.cnil-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

.cnil-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.cnil-info-item strong {
  color: #625CFB;
  margin-right: 8px;
  min-width: 120px;
}

.cnil-ref-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #625CFB;
}

.cnil-ref-item strong {
  color: #625CFB;
}

.cnil-nature {
  font-weight: 600;
  color: #625CFB;
}

.cnil-full-title {
  font-style: italic;
  flex: 1;
}

/* BOSS styles */
.boss-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-left: 20px;
}

.boss-date {
  font-size: 11px;
  background-color: #ede9fe;
  color: #625CFB;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
}

.boss-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.boss-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.boss-info-item strong {
  color: #625CFB;
  margin-right: 8px;
  min-width: 120px;
}

/* ACPR styles */
.acpr-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  margin-left: 20px;
}
.acpr-source-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #625CFB;
  color: white;
  font-size: 11px;
  font-weight: 500;
}
.acpr-date {
  font-size: 12px;
  color: #888;
}
.acpr-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #625CFB;
}
.acpr-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.acpr-info-item strong {
  color: #555;
  min-width: 90px;
}
.acpr-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}

/* AMF styles */
.amf-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  margin-left: 20px;
}
.amf-source-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #625CFB;
  color: white;
  font-size: 11px;
  font-weight: 500;
}
.amf-date {
  font-size: 12px;
  color: #888;
}
.amf-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #625CFB;
}
.amf-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.amf-info-item strong {
  color: #555;
  min-width: 90px;
}
.amf-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}

/* ECCOMP (Commission Européenne - Concurrence) styles */
.eccomp-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  margin-left: 20px;
}
.eccomp-instrument-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #625CFB;
  color: white;
  font-size: 11px;
  font-weight: 500;
}
.eccomp-case-number {
  font-size: 12px;
  color: #555;
  font-weight: 500;
}
.eccomp-date {
  font-size: 12px;
  color: #888;
}
.eccomp-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #625CFB;
}
.eccomp-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.eccomp-info-item strong {
  color: #555;
  min-width: 140px;
}
.eccomp-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}

.upc-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  margin-left: 20px;
  flex-wrap: wrap;
}
.upc-court-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #625CFB;
  color: white;
  font-size: 11px;
  font-weight: 500;
}
.upc-action-type {
  font-size: 12px;
  color: #555;
  font-weight: 500;
}
.upc-date {
  font-size: 12px;
  color: #888;
}
.upc-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #625CFB;
}
.upc-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.upc-info-item strong {
  color: #555;
  min-width: 140px;
}
.upc-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}
.upc-headnotes {
  margin-top: 16px;
}
.upc-keywords {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f0eefc;
  border-radius: 6px;
  font-size: 12px;
  color: #555;
}
.upc-keywords strong {
  color: #625CFB;
  margin-right: 8px;
}

.edpb-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  margin-left: 20px;
  flex-wrap: wrap;
}
.edpb-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #003399;
  color: white;
  font-size: 11px;
  font-weight: 500;
}
.edpb-date {
  font-size: 12px;
  color: #888;
}
.edpb-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #003399;
}
.edpb-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.edpb-info-item strong {
  color: #555;
  min-width: 140px;
}
.edpb-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}
.edpb-key-points {
  margin-top: 16px;
}
.edpb-keywords {
  margin-top: 12px;
  padding: 8px 12px;
  background: #eaeffb;
  border-radius: 6px;
  font-size: 12px;
  color: #555;
}
.edpb-keywords strong {
  color: #003399;
  margin-right: 8px;
}

</style>
